/**
 * Related editorial links shown on high-traffic product listing pages.
 *
 * Generated by `yarn plp-editorial-links`. Bag guide titles and hrefs come
 * from the guide-index block in content/geo/bags-plp/<locale>/bags.md. Icon
 * titles are the live page H1. Glossary titles are GLOSSARY_TERMS terms.
 *
 * Adding a PLP later is a data change: edit the selections in
 * scripts/plp-editorial-links.ts and re-run with --write. The component
 * looks this file up by (locale, listing-page url) and does not need a
 * code edit.
 *
 * Do not put em or en dashes in titles. Non-EN hrefs carry the locale
 * prefix. There are no English-article fallbacks in this set; every locale
 * points at a localised blog slug, icon page, or glossary anchor.
 */

export const PLP_EDITORIAL_LOCALES = [
  "en",
  "nl",
  "de",
  "fr",
  "es",
  "it",
] as const;

export type PlpEditorialLocale = (typeof PLP_EDITORIAL_LOCALES)[number];

export type PlpEditorialImage = {
  url: string;
  width: number;
  height: number;
  alt: string;
};

export type PlpEditorialLink = {
  href: string;
  title: string;
  /** Blog guides only: cover image, Strapi small format. Rendered as a card. */
  image?: PlpEditorialImage;
  /** Blog guides only: body word count / 200, at least 1. */
  readingMinutes?: number;
};

export type PlpEditorialEntry = {
  /** Canonical English PLP path, e.g. /bags/tote-bags. */
  key: string;
  /** Localized listing-page path per locale (no locale prefix). */
  urls: Record<PlpEditorialLocale, string>;
  links: Record<PlpEditorialLocale, PlpEditorialLink[]>;
};

export const PLP_EDITORIAL_ENTRIES: PlpEditorialEntry[] = [
  {
    key: "/furniture",
    urls: {
      en: "/furniture",
      nl: "/meubels",
      de: "/mobel",
      fr: "/meubles",
      es: "/muebles",
      it: "/mobili",
    },
    links: {
      en: [
        {
          href: "/icon/eames-lounge-chair",
          title: "Eames Lounge Chair",
        },
        {
          href: "/icon/barcelona-chair",
          title: "Barcelona Chair",
        },
        {
          href: "/icon/wishbone-chair",
          title: "Wishbone Chair",
        },
        {
          href: "/icon/ligne-roset-togo",
          title: "Ligne Roset Togo",
        },
        {
          href: "/glossary#lounge-chair",
          title: "Lounge chair",
        },
        {
          href: "/glossary#sideboard",
          title: "Sideboard",
        },
      ],
      nl: [
        {
          href: "/nl/icon/eames-lounge-chair",
          title: "Eames Lounge Chair",
        },
        {
          href: "/nl/icon/barcelona-chair",
          title: "Barcelona Chair",
        },
        {
          href: "/nl/icon/ligne-roset-togo",
          title: "Ligne Roset Togo",
        },
        {
          href: "/nl/glossary#lounge-chair",
          title: "Lounge chair",
        },
        {
          href: "/nl/glossary#sideboard",
          title: "Sideboard",
        },
      ],
      de: [
        {
          href: "/de/icon/eames-lounge-chair",
          title: "Eames Lounge Chair",
        },
        {
          href: "/de/icon/barcelona-chair",
          title: "Barcelona Chair",
        },
        {
          href: "/de/icon/ligne-roset-togo",
          title: "Ligne Roset Togo",
        },
        {
          href: "/de/glossary#lounge-chair",
          title: "Lounge chair",
        },
        {
          href: "/de/glossary#sideboard",
          title: "Sideboard",
        },
      ],
      fr: [
        {
          href: "/fr/icon/eames-lounge-chair",
          title: "Eames Lounge Chair",
        },
        {
          href: "/fr/icon/barcelona-chair",
          title: "Barcelona Chair",
        },
        {
          href: "/fr/icon/ligne-roset-togo",
          title: "Ligne Roset Togo",
        },
        {
          href: "/fr/glossary#lounge-chair",
          title: "Lounge chair",
        },
        {
          href: "/fr/glossary#sideboard",
          title: "Sideboard",
        },
      ],
      es: [
        {
          href: "/es/icon/eames-lounge-chair",
          title: "Eames Lounge Chair",
        },
        {
          href: "/es/icon/barcelona-chair",
          title: "Barcelona Chair",
        },
        {
          href: "/es/icon/ligne-roset-togo",
          title: "Ligne Roset Togo",
        },
        {
          href: "/es/glossary#lounge-chair",
          title: "Lounge chair",
        },
        {
          href: "/es/glossary#sideboard",
          title: "Sideboard",
        },
      ],
      it: [
        {
          href: "/it/icon/eames-lounge-chair",
          title: "Eames Lounge Chair",
        },
        {
          href: "/it/icon/barcelona-chair",
          title: "Barcelona Chair",
        },
        {
          href: "/it/icon/ligne-roset-togo",
          title: "Ligne Roset Togo",
        },
        {
          href: "/it/glossary#lounge-chair",
          title: "Lounge chair",
        },
        {
          href: "/it/glossary#sideboard",
          title: "Sideboard",
        },
      ],
    },
  },
  {
    key: "/furniture/sofas",
    urls: {
      en: "/furniture/sofas",
      nl: "/meubels/banken",
      de: "/mobel/sofas",
      fr: "/meubles/canapes",
      es: "/muebles/sofas",
      it: "/mobili/divani",
    },
    links: {
      en: [
        {
          href: "/icon/ligne-roset-togo",
          title: "Ligne Roset Togo",
        },
        {
          href: "/icon/cassina-maralunga",
          title: "Cassina Maralunga",
        },
        {
          href: "/icon/tufty-time",
          title: "B&B Italia Tufty-Time",
        },
        {
          href: "/icon/chesterfield-sofa",
          title: "Chesterfield Sofa",
        },
        {
          href: "/glossary#modular-sofa",
          title: "Modular sofa",
        },
        {
          href: "/glossary#settee",
          title: "Settee",
        },
        {
          href: "/glossary#sleeper-sofa",
          title: "Sleeper sofa",
        },
      ],
      nl: [
        {
          href: "/nl/icon/ligne-roset-togo",
          title: "Ligne Roset Togo",
        },
        {
          href: "/nl/icon/cassina-maralunga",
          title: "Cassina Maralunga",
        },
        {
          href: "/nl/icon/tufty-time",
          title: "B&B Italia Tufty-Time",
        },
        {
          href: "/nl/icon/chesterfield-sofa",
          title: "Chesterfield Sofa",
        },
        {
          href: "/nl/glossary#modular-sofa",
          title: "Modular sofa",
        },
        {
          href: "/nl/glossary#settee",
          title: "Settee",
        },
        {
          href: "/nl/glossary#sleeper-sofa",
          title: "Sleeper sofa",
        },
      ],
      de: [
        {
          href: "/de/icon/ligne-roset-togo",
          title: "Ligne Roset Togo",
        },
        {
          href: "/de/icon/cassina-maralunga",
          title: "Cassina Maralunga",
        },
        {
          href: "/de/icon/tufty-time",
          title: "B&B Italia Tufty-Time",
        },
        {
          href: "/de/icon/chesterfield-sofa",
          title: "Chesterfield Sofa",
        },
        {
          href: "/de/glossary#modular-sofa",
          title: "Modular sofa",
        },
        {
          href: "/de/glossary#settee",
          title: "Settee",
        },
        {
          href: "/de/glossary#sleeper-sofa",
          title: "Sleeper sofa",
        },
      ],
      fr: [
        {
          href: "/fr/icon/ligne-roset-togo",
          title: "Ligne Roset Togo",
        },
        {
          href: "/fr/icon/cassina-maralunga",
          title: "Cassina Maralunga",
        },
        {
          href: "/fr/icon/tufty-time",
          title: "B&B Italia Tufty-Time",
        },
        {
          href: "/fr/icon/chesterfield-sofa",
          title: "Chesterfield Sofa",
        },
        {
          href: "/fr/glossary#modular-sofa",
          title: "Modular sofa",
        },
        {
          href: "/fr/glossary#settee",
          title: "Settee",
        },
        {
          href: "/fr/glossary#sleeper-sofa",
          title: "Sleeper sofa",
        },
      ],
      es: [
        {
          href: "/es/icon/ligne-roset-togo",
          title: "Ligne Roset Togo",
        },
        {
          href: "/es/icon/cassina-maralunga",
          title: "Cassina Maralunga",
        },
        {
          href: "/es/icon/tufty-time",
          title: "B&B Italia Tufty-Time",
        },
        {
          href: "/es/icon/chesterfield-sofa",
          title: "Chesterfield Sofa",
        },
        {
          href: "/es/glossary#modular-sofa",
          title: "Modular sofa",
        },
        {
          href: "/es/glossary#settee",
          title: "Settee",
        },
        {
          href: "/es/glossary#sleeper-sofa",
          title: "Sleeper sofa",
        },
      ],
      it: [
        {
          href: "/it/icon/ligne-roset-togo",
          title: "Ligne Roset Togo",
        },
        {
          href: "/it/icon/cassina-maralunga",
          title: "Cassina Maralunga",
        },
        {
          href: "/it/icon/tufty-time",
          title: "B&B Italia Tufty-Time",
        },
        {
          href: "/it/icon/chesterfield-sofa",
          title: "Chesterfield Sofa",
        },
        {
          href: "/it/glossary#modular-sofa",
          title: "Modular sofa",
        },
        {
          href: "/it/glossary#settee",
          title: "Settee",
        },
        {
          href: "/it/glossary#sleeper-sofa",
          title: "Sleeper sofa",
        },
      ],
    },
  },
  {
    key: "/lighting",
    urls: {
      en: "/lighting",
      nl: "/verlichting",
      de: "/beleuchtung",
      fr: "/luminaires",
      es: "/iluminacion",
      it: "/illuminazione",
    },
    links: {
      en: [
        {
          href: "/icon/arco-lamp",
          title: "Arco Lamp",
        },
        {
          href: "/icon/flowerpot-lamp",
          title: "Flowerpot Lamp",
        },
        {
          href: "/icon/akari-lamp",
          title: "Akari Lamp",
        },
        {
          href: "/icon/artemide-tolomeo",
          title: "Artemide Tolomeo",
        },
        {
          href: "/glossary#arco",
          title: "Arco",
        },
        {
          href: "/glossary#chandelier",
          title: "Chandelier",
        },
      ],
      nl: [
        {
          href: "/nl/icon/arco-lamp",
          title: "Arco Lamp",
        },
        {
          href: "/nl/icon/flowerpot-lamp",
          title: "Flowerpot Lamp",
        },
        {
          href: "/nl/icon/akari-lamp",
          title: "Akari Lamp",
        },
        {
          href: "/nl/icon/artemide-tolomeo",
          title: "Artemide Tolomeo",
        },
        {
          href: "/nl/glossary#arco",
          title: "Arco",
        },
        {
          href: "/nl/glossary#chandelier",
          title: "Chandelier",
        },
      ],
      de: [
        {
          href: "/de/icon/arco-lamp",
          title: "Arco Lamp",
        },
        {
          href: "/de/icon/flowerpot-lamp",
          title: "Flowerpot Lamp",
        },
        {
          href: "/de/icon/akari-lamp",
          title: "Akari Lamp",
        },
        {
          href: "/de/icon/artemide-tolomeo",
          title: "Artemide Tolomeo",
        },
        {
          href: "/de/glossary#arco",
          title: "Arco",
        },
        {
          href: "/de/glossary#chandelier",
          title: "Chandelier",
        },
      ],
      fr: [
        {
          href: "/fr/icon/arco-lamp",
          title: "Arco Lamp",
        },
        {
          href: "/fr/icon/flowerpot-lamp",
          title: "Flowerpot Lamp",
        },
        {
          href: "/fr/icon/akari-lamp",
          title: "Akari Lamp",
        },
        {
          href: "/fr/icon/artemide-tolomeo",
          title: "Artemide Tolomeo",
        },
        {
          href: "/fr/glossary#arco",
          title: "Arco",
        },
        {
          href: "/fr/glossary#chandelier",
          title: "Chandelier",
        },
      ],
      es: [
        {
          href: "/es/icon/arco-lamp",
          title: "Arco Lamp",
        },
        {
          href: "/es/icon/flowerpot-lamp",
          title: "Flowerpot Lamp",
        },
        {
          href: "/es/icon/akari-lamp",
          title: "Akari Lamp",
        },
        {
          href: "/es/icon/artemide-tolomeo",
          title: "Artemide Tolomeo",
        },
        {
          href: "/es/glossary#arco",
          title: "Arco",
        },
        {
          href: "/es/glossary#chandelier",
          title: "Chandelier",
        },
      ],
      it: [
        {
          href: "/it/icon/arco-lamp",
          title: "Arco Lamp",
        },
        {
          href: "/it/icon/flowerpot-lamp",
          title: "Flowerpot Lamp",
        },
        {
          href: "/it/icon/akari-lamp",
          title: "Akari Lamp",
        },
        {
          href: "/it/icon/artemide-tolomeo",
          title: "Artemide Tolomeo",
        },
        {
          href: "/it/glossary#arco",
          title: "Arco",
        },
        {
          href: "/it/glossary#chandelier",
          title: "Chandelier",
        },
      ],
    },
  },
  {
    key: "/art",
    urls: {
      en: "/art",
      nl: "/kunst",
      de: "/kunst",
      fr: "/objets-dart",
      es: "/arte",
      it: "/arte",
    },
    links: {
      en: [
        {
          href: "/glossary#lithograph",
          title: "Lithograph",
        },
        {
          href: "/glossary#oil-on-canvas",
          title: "Oil on canvas",
        },
        {
          href: "/glossary#limited-edition-print",
          title: "Limited edition print",
        },
        {
          href: "/glossary#serigraph",
          title: "Serigraph",
        },
        {
          href: "/glossary#op-art",
          title: "Op Art",
        },
      ],
      nl: [
        {
          href: "/nl/glossary#lithograph",
          title: "Lithograph",
        },
        {
          href: "/nl/glossary#oil-on-canvas",
          title: "Oil on canvas",
        },
        {
          href: "/nl/glossary#limited-edition-print",
          title: "Limited edition print",
        },
        {
          href: "/nl/glossary#serigraph",
          title: "Serigraph",
        },
        {
          href: "/nl/glossary#op-art",
          title: "Op Art",
        },
      ],
      de: [
        {
          href: "/de/glossary#lithograph",
          title: "Lithograph",
        },
        {
          href: "/de/glossary#oil-on-canvas",
          title: "Oil on canvas",
        },
        {
          href: "/de/glossary#limited-edition-print",
          title: "Limited edition print",
        },
        {
          href: "/de/glossary#serigraph",
          title: "Serigraph",
        },
        {
          href: "/de/glossary#op-art",
          title: "Op Art",
        },
      ],
      fr: [
        {
          href: "/fr/glossary#lithograph",
          title: "Lithograph",
        },
        {
          href: "/fr/glossary#oil-on-canvas",
          title: "Oil on canvas",
        },
        {
          href: "/fr/glossary#limited-edition-print",
          title: "Limited edition print",
        },
        {
          href: "/fr/glossary#serigraph",
          title: "Serigraph",
        },
        {
          href: "/fr/glossary#op-art",
          title: "Op Art",
        },
      ],
      es: [
        {
          href: "/es/glossary#lithograph",
          title: "Lithograph",
        },
        {
          href: "/es/glossary#oil-on-canvas",
          title: "Oil on canvas",
        },
        {
          href: "/es/glossary#limited-edition-print",
          title: "Limited edition print",
        },
        {
          href: "/es/glossary#serigraph",
          title: "Serigraph",
        },
        {
          href: "/es/glossary#op-art",
          title: "Op Art",
        },
      ],
      it: [
        {
          href: "/it/glossary#lithograph",
          title: "Lithograph",
        },
        {
          href: "/it/glossary#oil-on-canvas",
          title: "Oil on canvas",
        },
        {
          href: "/it/glossary#limited-edition-print",
          title: "Limited edition print",
        },
        {
          href: "/it/glossary#serigraph",
          title: "Serigraph",
        },
        {
          href: "/it/glossary#op-art",
          title: "Op Art",
        },
      ],
    },
  },
  {
    key: "/decoration",
    urls: {
      en: "/decoration",
      nl: "/decoratie",
      de: "/dekoration",
      fr: "/decoration",
      es: "/decoracion",
      it: "/decorazione",
    },
    links: {
      en: [
        {
          href: "/icon/mushroom-lamp",
          title: "Mushroom Lamp",
        },
        {
          href: "/icon/lotus-lamp",
          title: "Lotus Lamp",
        },
        {
          href: "/glossary#art-deco",
          title: "Art Deco",
        },
        {
          href: "/glossary#memphis",
          title: "Memphis",
        },
        {
          href: "/glossary#eclectic",
          title: "Eclectic",
        },
      ],
      nl: [
        {
          href: "/nl/icon/mushroom-lamp",
          title: "Mushroom Lamp",
        },
        {
          href: "/nl/icon/lotus-lamp",
          title: "Lotus Lamp",
        },
        {
          href: "/nl/glossary#art-deco",
          title: "Art Deco",
        },
        {
          href: "/nl/glossary#memphis",
          title: "Memphis",
        },
        {
          href: "/nl/glossary#eclectic",
          title: "Eclectic",
        },
      ],
      de: [
        {
          href: "/de/icon/mushroom-lamp",
          title: "Mushroom Lamp",
        },
        {
          href: "/de/icon/lotus-lamp",
          title: "Lotus Lamp",
        },
        {
          href: "/de/glossary#art-deco",
          title: "Art Deco",
        },
        {
          href: "/de/glossary#memphis",
          title: "Memphis",
        },
        {
          href: "/de/glossary#eclectic",
          title: "Eclectic",
        },
      ],
      fr: [
        {
          href: "/fr/icon/mushroom-lamp",
          title: "Mushroom Lamp",
        },
        {
          href: "/fr/icon/lotus-lamp",
          title: "Lotus Lamp",
        },
        {
          href: "/fr/glossary#art-deco",
          title: "Art Deco",
        },
        {
          href: "/fr/glossary#memphis",
          title: "Memphis",
        },
        {
          href: "/fr/glossary#eclectic",
          title: "Eclectic",
        },
      ],
      es: [
        {
          href: "/es/icon/mushroom-lamp",
          title: "Mushroom Lamp",
        },
        {
          href: "/es/icon/lotus-lamp",
          title: "Lotus Lamp",
        },
        {
          href: "/es/glossary#art-deco",
          title: "Art Deco",
        },
        {
          href: "/es/glossary#memphis",
          title: "Memphis",
        },
        {
          href: "/es/glossary#eclectic",
          title: "Eclectic",
        },
      ],
      it: [
        {
          href: "/it/icon/mushroom-lamp",
          title: "Mushroom Lamp",
        },
        {
          href: "/it/icon/lotus-lamp",
          title: "Lotus Lamp",
        },
        {
          href: "/it/glossary#art-deco",
          title: "Art Deco",
        },
        {
          href: "/it/glossary#memphis",
          title: "Memphis",
        },
        {
          href: "/it/glossary#eclectic",
          title: "Eclectic",
        },
      ],
    },
  },
  {
    key: "/bags",
    urls: {
      en: "/bags",
      nl: "/tassen",
      de: "/taschen",
      fr: "/sacs",
      es: "/bolsos",
      it: "/borse",
    },
    links: {
      en: [
        {
          href: "/blog/secondhand-designer-handbags-guide",
          title:
            "The Definitive Guide to Buying Secondhand Designer Handbags in Europe",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/blog/how-whoppah-curates-every-bag",
          title:
            "How Whoppah Curates Every Bag: What We Check, Reject and Cover",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_whoppah_curates_every_bag_ai_a764d3af05.jpg",
            width: 500,
            height: 281,
            alt: "Gloved hands inspecting the stitching of a tan leather handbag",
          },
        },
        {
          href: "/blog/handbag-authentication-guide",
          title:
            "How to Authenticate a Designer Handbag: The Cross-Brand Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/blog/top-10-designer-handbag-brands",
          title:
            "The Top 10 Designer Handbag Brands for Buying Secondhand, Ranked",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_top_10_designer_handbag_brands_ai_f21333e72a.jpg",
            width: 500,
            height: 281,
            alt: "Four elegant leather handbags in a row on beige linen",
          },
        },
        {
          href: "/blog/where-to-buy-secondhand-designer-bags-europe",
          title:
            "Where to Buy Secondhand Designer Bags in Europe: 4 Platforms Compared",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_where_to_buy_secondhand_designer_bags_europe_ai_02370c7c36.jpg",
            width: 500,
            height: 281,
            alt: "Two elegant handbags on a marble console in window light",
          },
        },
        {
          href: "/blog/best-investment-handbags-2026",
          title: "The Handbags That Hold Their Value in 2026 (And Why)",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_best_investment_handbags_2026_ai_0ea1e55071.jpg",
            width: 500,
            height: 281,
            alt: "Structured leather handbag spotlit on dark green velvet",
          },
        },
        {
          href: "/glossary#birkin",
          title: "Birkin",
        },
        {
          href: "/glossary#classic-flap",
          title: "Classic Flap",
        },
        {
          href: "/glossary#clutch",
          title: "Clutch",
        },
        {
          href: "/glossary#caviar-leather",
          title: "Caviar leather",
        },
      ],
      nl: [
        {
          href: "/nl/blog/tweedehands-designertas-kopen-complete-gids-nl",
          title:
            "De complete gids voor het kopen van een tweedehands designertas in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/nl/blog/zo-cureert-whoppah-elke-tas-nl",
          title:
            "Zo cureert Whoppah elke tas: wat we controleren, afkeuren en dekken",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_whoppah_curates_every_bag_ai_a764d3af05.jpg",
            width: 500,
            height: 281,
            alt: "Gloved hands inspecting the stitching of a tan leather handbag",
          },
        },
        {
          href: "/nl/blog/designertas-authenticiteit-controleren-gids-nl",
          title:
            "Hoe herken je een echte designertas? De gids voor alle merken",
          readingMinutes: 12,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/nl/blog/beste-designertas-merken-tweedehands-nl",
          title:
            "De top 10 designertasmerken om tweedehands te kopen, gerangschikt",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_top_10_designer_handbag_brands_ai_f21333e72a.jpg",
            width: 500,
            height: 281,
            alt: "Four elegant leather handbags in a row on beige linen",
          },
        },
        {
          href: "/nl/blog/waar-tweedehands-designertas-kopen-europa-nl",
          title:
            "Waar koop je een tweedehands designertas in Europa? 4 platforms vergeleken",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_where_to_buy_secondhand_designer_bags_europe_ai_02370c7c36.jpg",
            width: 500,
            height: 281,
            alt: "Two elegant handbags on a marble console in window light",
          },
        },
        {
          href: "/nl/blog/designertassen-waardevast-2026-nl",
          title: "Welke designertassen behouden hun waarde in 2026 (en waarom)",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_best_investment_handbags_2026_ai_0ea1e55071.jpg",
            width: 500,
            height: 281,
            alt: "Structured leather handbag spotlit on dark green velvet",
          },
        },
        {
          href: "/nl/glossary#birkin",
          title: "Birkin",
        },
        {
          href: "/nl/glossary#classic-flap",
          title: "Classic Flap",
        },
        {
          href: "/nl/glossary#clutch",
          title: "Clutch",
        },
        {
          href: "/nl/glossary#caviar-leather",
          title: "Caviar leather",
        },
      ],
      de: [
        {
          href: "/de/blog/gebrauchte-designertasche-kaufen-guide-de",
          title:
            "Der umfassende Guide zum Kauf gebrauchter Designertaschen in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/de/blog/so-kuratiert-whoppah-jede-tasche-de",
          title:
            "So kuratiert Whoppah jede Tasche: was wir prüfen, ablehnen und absichern",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_whoppah_curates_every_bag_ai_a764d3af05.jpg",
            width: 500,
            height: 281,
            alt: "Gloved hands inspecting the stitching of a tan leather handbag",
          },
        },
        {
          href: "/de/blog/designertasche-echtheit-pruefen-guide-de",
          title:
            "Designertasche auf Echtheit prüfen: der markenübergreifende Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/de/blog/beste-designertaschen-marken-gebraucht-de",
          title:
            "Die Top 10 Designertaschen-Marken für den Gebrauchtkauf, im Ranking",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_top_10_designer_handbag_brands_ai_f21333e72a.jpg",
            width: 500,
            height: 281,
            alt: "Four elegant leather handbags in a row on beige linen",
          },
        },
        {
          href: "/de/blog/gebrauchte-designertaschen-kaufen-europa-de",
          title:
            "Wo kauft man gebrauchte Designertaschen in Europa? 4 Plattformen im Vergleich",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_where_to_buy_secondhand_designer_bags_europe_ai_02370c7c36.jpg",
            width: 500,
            height: 281,
            alt: "Two elegant handbags on a marble console in window light",
          },
        },
        {
          href: "/de/blog/designertaschen-wertstabil-2026-de",
          title: "Welche Designertaschen 2026 ihren Wert halten (und warum)",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_best_investment_handbags_2026_ai_0ea1e55071.jpg",
            width: 500,
            height: 281,
            alt: "Structured leather handbag spotlit on dark green velvet",
          },
        },
        {
          href: "/de/glossary#birkin",
          title: "Birkin",
        },
        {
          href: "/de/glossary#classic-flap",
          title: "Classic Flap",
        },
        {
          href: "/de/glossary#clutch",
          title: "Clutch",
        },
        {
          href: "/de/glossary#caviar-leather",
          title: "Caviar leather",
        },
      ],
      fr: [
        {
          href: "/fr/blog/acheter-sac-de-luxe-seconde-main-guide-fr",
          title:
            "Le guide complet pour acheter un sac de luxe seconde main en Europe",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/fr/blog/comment-whoppah-selectionne-chaque-sac-fr",
          title:
            "Comment Whoppah sélectionne chaque sac : ce que nous vérifions, refusons et couvrons",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_whoppah_curates_every_bag_ai_a764d3af05.jpg",
            width: 500,
            height: 281,
            alt: "Gloved hands inspecting the stitching of a tan leather handbag",
          },
        },
        {
          href: "/fr/blog/authentifier-un-sac-de-luxe-guide-fr",
          title: "Comment authentifier un sac de luxe : le guide multimarques",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/fr/blog/meilleures-marques-sacs-luxe-seconde-main-fr",
          title:
            "Le top 10 des marques de sacs de luxe à acheter en seconde main",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_top_10_designer_handbag_brands_ai_f21333e72a.jpg",
            width: 500,
            height: 281,
            alt: "Four elegant leather handbags in a row on beige linen",
          },
        },
        {
          href: "/fr/blog/ou-acheter-sac-de-luxe-seconde-main-europe-fr",
          title:
            "Où acheter un sac de luxe seconde main en Europe ? 4 plateformes comparées",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_where_to_buy_secondhand_designer_bags_europe_ai_02370c7c36.jpg",
            width: 500,
            height: 281,
            alt: "Two elegant handbags on a marble console in window light",
          },
        },
        {
          href: "/fr/blog/sacs-de-luxe-valeur-2026-fr",
          title:
            "Les sacs de luxe qui gardent leur valeur en 2026 (et pourquoi)",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_best_investment_handbags_2026_ai_0ea1e55071.jpg",
            width: 500,
            height: 281,
            alt: "Structured leather handbag spotlit on dark green velvet",
          },
        },
        {
          href: "/fr/glossary#birkin",
          title: "Birkin",
        },
        {
          href: "/fr/glossary#classic-flap",
          title: "Classic Flap",
        },
        {
          href: "/fr/glossary#clutch",
          title: "Clutch",
        },
        {
          href: "/fr/glossary#caviar-leather",
          title: "Caviar leather",
        },
      ],
      es: [
        {
          href: "/es/blog/comprar-bolso-de-lujo-segunda-mano-guia-es",
          title:
            "La guía definitiva para comprar un bolso de lujo de segunda mano en Europa",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/es/blog/como-selecciona-whoppah-cada-bolso-es",
          title:
            "Cómo selecciona Whoppah cada bolso: qué revisamos, qué rechazamos y qué cubrimos",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_whoppah_curates_every_bag_ai_a764d3af05.jpg",
            width: 500,
            height: 281,
            alt: "Gloved hands inspecting the stitching of a tan leather handbag",
          },
        },
        {
          href: "/es/blog/autenticar-un-bolso-de-lujo-guia-es",
          title:
            "Cómo autenticar un bolso de lujo: la guía para todas las marcas",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/es/blog/mejores-marcas-bolsos-lujo-segunda-mano-es",
          title:
            "Las 10 mejores marcas de bolsos de lujo para comprar de segunda mano",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_top_10_designer_handbag_brands_ai_f21333e72a.jpg",
            width: 500,
            height: 281,
            alt: "Four elegant leather handbags in a row on beige linen",
          },
        },
        {
          href: "/es/blog/donde-comprar-bolsos-lujo-segunda-mano-europa-es",
          title:
            "Dónde comprar bolsos de lujo de segunda mano en Europa: 4 plataformas comparadas",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_where_to_buy_secondhand_designer_bags_europe_ai_02370c7c36.jpg",
            width: 500,
            height: 281,
            alt: "Two elegant handbags on a marble console in window light",
          },
        },
        {
          href: "/es/blog/bolsos-que-mantienen-valor-2026-es",
          title: "Los bolsos que mantienen su valor en 2026 (y por qué)",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_best_investment_handbags_2026_ai_0ea1e55071.jpg",
            width: 500,
            height: 281,
            alt: "Structured leather handbag spotlit on dark green velvet",
          },
        },
        {
          href: "/es/glossary#birkin",
          title: "Birkin",
        },
        {
          href: "/es/glossary#classic-flap",
          title: "Classic Flap",
        },
        {
          href: "/es/glossary#clutch",
          title: "Clutch",
        },
        {
          href: "/es/glossary#caviar-leather",
          title: "Caviar leather",
        },
      ],
      it: [
        {
          href: "/it/blog/comprare-borsa-firmata-usata-guida-it",
          title:
            "La guida definitiva per comprare una borsa firmata usata in Europa",
          readingMinutes: 15,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/it/blog/come-whoppah-seleziona-ogni-borsa-it",
          title:
            "Come Whoppah seleziona ogni borsa: cosa controlliamo, cosa rifiutiamo e cosa copriamo",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_whoppah_curates_every_bag_ai_a764d3af05.jpg",
            width: 500,
            height: 281,
            alt: "Gloved hands inspecting the stitching of a tan leather handbag",
          },
        },
        {
          href: "/it/blog/autenticare-una-borsa-firmata-guida-it",
          title:
            "Come autenticare una borsa firmata: la guida per tutte le maison",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/it/blog/migliori-marche-borse-firmate-usate-it",
          title:
            "Le 10 migliori maison di borse da comprare usate, in classifica",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_top_10_designer_handbag_brands_ai_f21333e72a.jpg",
            width: 500,
            height: 281,
            alt: "Four elegant leather handbags in a row on beige linen",
          },
        },
        {
          href: "/it/blog/dove-comprare-borse-firmate-usate-europa-it",
          title:
            "Dove comprare borse firmate usate in Europa: 4 piattaforme a confronto",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_where_to_buy_secondhand_designer_bags_europe_ai_02370c7c36.jpg",
            width: 500,
            height: 281,
            alt: "Two elegant handbags on a marble console in window light",
          },
        },
        {
          href: "/it/blog/borse-che-mantengono-valore-2026-it",
          title: "Le borse che mantengono il valore nel 2026 (e perché)",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_best_investment_handbags_2026_ai_0ea1e55071.jpg",
            width: 500,
            height: 281,
            alt: "Structured leather handbag spotlit on dark green velvet",
          },
        },
        {
          href: "/it/glossary#birkin",
          title: "Birkin",
        },
        {
          href: "/it/glossary#classic-flap",
          title: "Classic Flap",
        },
        {
          href: "/it/glossary#clutch",
          title: "Clutch",
        },
        {
          href: "/it/glossary#caviar-leather",
          title: "Caviar leather",
        },
      ],
    },
  },
  {
    key: "/bags/backpacks",
    urls: {
      en: "/bags/backpacks",
      nl: "/tassen/rugzakken",
      de: "/taschen/rucksacke",
      fr: "/sacs/sacs-a-dos",
      es: "/bolsos/mochilas",
      it: "/borse/zaini",
    },
    links: {
      en: [
        {
          href: "/blog/secondhand-designer-handbags-guide",
          title:
            "The Definitive Guide to Buying Secondhand Designer Handbags in Europe",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/blog/handbag-authentication-guide",
          title:
            "How to Authenticate a Designer Handbag: The Cross-Brand Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/blog/how-to-care-for-leather-handbags",
          title:
            "How to Care for Leather Handbags: The Routine That Protects Their Value",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/tweedehands-designertas-kopen-complete-gids-nl",
          title:
            "De complete gids voor het kopen van een tweedehands designertas in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/nl/blog/designertas-authenticiteit-controleren-gids-nl",
          title:
            "Hoe herken je een echte designertas? De gids voor alle merken",
          readingMinutes: 12,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/nl/blog/leren-tassen-onderhouden-nl",
          title:
            "Leren tassen onderhouden: de routine die hun waarde beschermt",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/gebrauchte-designertasche-kaufen-guide-de",
          title:
            "Der umfassende Guide zum Kauf gebrauchter Designertaschen in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/de/blog/designertasche-echtheit-pruefen-guide-de",
          title:
            "Designertasche auf Echtheit prüfen: der markenübergreifende Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/de/blog/ledertaschen-pflegen-de",
          title: "Ledertaschen pflegen: die Routine, die ihren Wert schützt",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/acheter-sac-de-luxe-seconde-main-guide-fr",
          title:
            "Le guide complet pour acheter un sac de luxe seconde main en Europe",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/fr/blog/authentifier-un-sac-de-luxe-guide-fr",
          title: "Comment authentifier un sac de luxe : le guide multimarques",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/fr/blog/entretien-sac-en-cuir-fr",
          title: "Entretenir un sac en cuir : la routine qui protège sa valeur",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/comprar-bolso-de-lujo-segunda-mano-guia-es",
          title:
            "La guía definitiva para comprar un bolso de lujo de segunda mano en Europa",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/es/blog/autenticar-un-bolso-de-lujo-guia-es",
          title:
            "Cómo autenticar un bolso de lujo: la guía para todas las marcas",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/es/blog/cuidar-bolsos-de-piel-es",
          title:
            "Cómo cuidar los bolsos de piel: la rutina que protege su valor",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/comprare-borsa-firmata-usata-guida-it",
          title:
            "La guida definitiva per comprare una borsa firmata usata in Europa",
          readingMinutes: 15,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/it/blog/autenticare-una-borsa-firmata-guida-it",
          title:
            "Come autenticare una borsa firmata: la guida per tutte le maison",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/it/blog/come-curare-borse-in-pelle-it",
          title:
            "Come curare le borse in pelle: la routine che ne protegge il valore",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
    },
  },
  {
    key: "/bags/belt-bags",
    urls: {
      en: "/bags/belt-bags",
      nl: "/tassen/riemtassen",
      de: "/taschen/gurteltaschen",
      fr: "/sacs/sacs-ceinture",
      es: "/bolsos/bolsos-de-cinturon",
      it: "/borse/marsupi",
    },
    links: {
      en: [
        {
          href: "/blog/secondhand-designer-handbags-guide",
          title:
            "The Definitive Guide to Buying Secondhand Designer Handbags in Europe",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/blog/handbag-authentication-guide",
          title:
            "How to Authenticate a Designer Handbag: The Cross-Brand Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/blog/how-to-care-for-leather-handbags",
          title:
            "How to Care for Leather Handbags: The Routine That Protects Their Value",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/tweedehands-designertas-kopen-complete-gids-nl",
          title:
            "De complete gids voor het kopen van een tweedehands designertas in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/nl/blog/designertas-authenticiteit-controleren-gids-nl",
          title:
            "Hoe herken je een echte designertas? De gids voor alle merken",
          readingMinutes: 12,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/nl/blog/leren-tassen-onderhouden-nl",
          title:
            "Leren tassen onderhouden: de routine die hun waarde beschermt",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/gebrauchte-designertasche-kaufen-guide-de",
          title:
            "Der umfassende Guide zum Kauf gebrauchter Designertaschen in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/de/blog/designertasche-echtheit-pruefen-guide-de",
          title:
            "Designertasche auf Echtheit prüfen: der markenübergreifende Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/de/blog/ledertaschen-pflegen-de",
          title: "Ledertaschen pflegen: die Routine, die ihren Wert schützt",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/acheter-sac-de-luxe-seconde-main-guide-fr",
          title:
            "Le guide complet pour acheter un sac de luxe seconde main en Europe",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/fr/blog/authentifier-un-sac-de-luxe-guide-fr",
          title: "Comment authentifier un sac de luxe : le guide multimarques",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/fr/blog/entretien-sac-en-cuir-fr",
          title: "Entretenir un sac en cuir : la routine qui protège sa valeur",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/comprar-bolso-de-lujo-segunda-mano-guia-es",
          title:
            "La guía definitiva para comprar un bolso de lujo de segunda mano en Europa",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/es/blog/autenticar-un-bolso-de-lujo-guia-es",
          title:
            "Cómo autenticar un bolso de lujo: la guía para todas las marcas",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/es/blog/cuidar-bolsos-de-piel-es",
          title:
            "Cómo cuidar los bolsos de piel: la rutina que protege su valor",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/comprare-borsa-firmata-usata-guida-it",
          title:
            "La guida definitiva per comprare una borsa firmata usata in Europa",
          readingMinutes: 15,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/it/blog/autenticare-una-borsa-firmata-guida-it",
          title:
            "Come autenticare una borsa firmata: la guida per tutte le maison",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/it/blog/come-curare-borse-in-pelle-it",
          title:
            "Come curare le borse in pelle: la routine che ne protegge il valore",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
    },
  },
  {
    key: "/bags/bucket-bags",
    urls: {
      en: "/bags/bucket-bags",
      nl: "/tassen/bucket-tassen",
      de: "/taschen/bucket-bags",
      fr: "/sacs/sacs-seau",
      es: "/bolsos/bolsos-cubo",
      it: "/borse/borse-a-secchiello",
    },
    links: {
      en: [
        {
          href: "/blog/secondhand-designer-handbags-guide",
          title:
            "The Definitive Guide to Buying Secondhand Designer Handbags in Europe",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/blog/handbag-authentication-guide",
          title:
            "How to Authenticate a Designer Handbag: The Cross-Brand Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/blog/how-to-care-for-leather-handbags",
          title:
            "How to Care for Leather Handbags: The Routine That Protects Their Value",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/tweedehands-designertas-kopen-complete-gids-nl",
          title:
            "De complete gids voor het kopen van een tweedehands designertas in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/nl/blog/designertas-authenticiteit-controleren-gids-nl",
          title:
            "Hoe herken je een echte designertas? De gids voor alle merken",
          readingMinutes: 12,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/nl/blog/leren-tassen-onderhouden-nl",
          title:
            "Leren tassen onderhouden: de routine die hun waarde beschermt",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/gebrauchte-designertasche-kaufen-guide-de",
          title:
            "Der umfassende Guide zum Kauf gebrauchter Designertaschen in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/de/blog/designertasche-echtheit-pruefen-guide-de",
          title:
            "Designertasche auf Echtheit prüfen: der markenübergreifende Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/de/blog/ledertaschen-pflegen-de",
          title: "Ledertaschen pflegen: die Routine, die ihren Wert schützt",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/acheter-sac-de-luxe-seconde-main-guide-fr",
          title:
            "Le guide complet pour acheter un sac de luxe seconde main en Europe",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/fr/blog/authentifier-un-sac-de-luxe-guide-fr",
          title: "Comment authentifier un sac de luxe : le guide multimarques",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/fr/blog/entretien-sac-en-cuir-fr",
          title: "Entretenir un sac en cuir : la routine qui protège sa valeur",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/comprar-bolso-de-lujo-segunda-mano-guia-es",
          title:
            "La guía definitiva para comprar un bolso de lujo de segunda mano en Europa",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/es/blog/autenticar-un-bolso-de-lujo-guia-es",
          title:
            "Cómo autenticar un bolso de lujo: la guía para todas las marcas",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/es/blog/cuidar-bolsos-de-piel-es",
          title:
            "Cómo cuidar los bolsos de piel: la rutina que protege su valor",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/comprare-borsa-firmata-usata-guida-it",
          title:
            "La guida definitiva per comprare una borsa firmata usata in Europa",
          readingMinutes: 15,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/it/blog/autenticare-una-borsa-firmata-guida-it",
          title:
            "Come autenticare una borsa firmata: la guida per tutte le maison",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/it/blog/come-curare-borse-in-pelle-it",
          title:
            "Come curare le borse in pelle: la routine che ne protegge il valore",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
    },
  },
  {
    key: "/bags/clutches",
    urls: {
      en: "/bags/clutches",
      nl: "/tassen/clutches",
      de: "/taschen/clutches",
      fr: "/sacs/pochettes",
      es: "/bolsos/clutches",
      it: "/borse/clutch",
    },
    links: {
      en: [
        {
          href: "/blog/secondhand-designer-handbags-guide",
          title:
            "The Definitive Guide to Buying Secondhand Designer Handbags in Europe",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/blog/handbag-authentication-guide",
          title:
            "How to Authenticate a Designer Handbag: The Cross-Brand Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/blog/chanel-classic-flap-guide",
          title:
            "Chanel Classic Flap Guide: Sizes, Leathers, Hardware and the 2.55 Question",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_classic_flap_guide_ai_267580a053.jpg",
            width: 500,
            height: 281,
            alt: "Black quilted flap bag with gold chain on a velvet chair",
          },
        },
        {
          href: "/blog/how-to-care-for-leather-handbags",
          title:
            "How to Care for Leather Handbags: The Routine That Protects Their Value",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/tweedehands-designertas-kopen-complete-gids-nl",
          title:
            "De complete gids voor het kopen van een tweedehands designertas in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/nl/blog/designertas-authenticiteit-controleren-gids-nl",
          title:
            "Hoe herken je een echte designertas? De gids voor alle merken",
          readingMinutes: 12,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/nl/blog/chanel-classic-flap-gids-nl",
          title:
            "Chanel Classic Flap gids: maten, leersoorten, hardware en de 2.55-vraag",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_classic_flap_guide_ai_267580a053.jpg",
            width: 500,
            height: 281,
            alt: "Black quilted flap bag with gold chain on a velvet chair",
          },
        },
        {
          href: "/nl/blog/leren-tassen-onderhouden-nl",
          title:
            "Leren tassen onderhouden: de routine die hun waarde beschermt",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/gebrauchte-designertasche-kaufen-guide-de",
          title:
            "Der umfassende Guide zum Kauf gebrauchter Designertaschen in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/de/blog/designertasche-echtheit-pruefen-guide-de",
          title:
            "Designertasche auf Echtheit prüfen: der markenübergreifende Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/de/blog/chanel-classic-flap-guide-de",
          title:
            "Chanel Classic Flap Guide: Größen, Leder, Hardware und die 2.55-Frage",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_classic_flap_guide_ai_267580a053.jpg",
            width: 500,
            height: 281,
            alt: "Black quilted flap bag with gold chain on a velvet chair",
          },
        },
        {
          href: "/de/blog/ledertaschen-pflegen-de",
          title: "Ledertaschen pflegen: die Routine, die ihren Wert schützt",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/acheter-sac-de-luxe-seconde-main-guide-fr",
          title:
            "Le guide complet pour acheter un sac de luxe seconde main en Europe",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/fr/blog/authentifier-un-sac-de-luxe-guide-fr",
          title: "Comment authentifier un sac de luxe : le guide multimarques",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/fr/blog/chanel-classic-flap-guide-fr",
          title:
            "Guide du Chanel Classic Flap : tailles, cuirs, métal et la question du 2.55",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_classic_flap_guide_ai_267580a053.jpg",
            width: 500,
            height: 281,
            alt: "Black quilted flap bag with gold chain on a velvet chair",
          },
        },
        {
          href: "/fr/blog/entretien-sac-en-cuir-fr",
          title: "Entretenir un sac en cuir : la routine qui protège sa valeur",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/comprar-bolso-de-lujo-segunda-mano-guia-es",
          title:
            "La guía definitiva para comprar un bolso de lujo de segunda mano en Europa",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/es/blog/autenticar-un-bolso-de-lujo-guia-es",
          title:
            "Cómo autenticar un bolso de lujo: la guía para todas las marcas",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/es/blog/chanel-classic-flap-guia-es",
          title:
            "Guía del Chanel Classic Flap: tallas, pieles, herrajes y la cuestión del 2.55",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_classic_flap_guide_ai_267580a053.jpg",
            width: 500,
            height: 281,
            alt: "Black quilted flap bag with gold chain on a velvet chair",
          },
        },
        {
          href: "/es/blog/cuidar-bolsos-de-piel-es",
          title:
            "Cómo cuidar los bolsos de piel: la rutina que protege su valor",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/comprare-borsa-firmata-usata-guida-it",
          title:
            "La guida definitiva per comprare una borsa firmata usata in Europa",
          readingMinutes: 15,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/it/blog/autenticare-una-borsa-firmata-guida-it",
          title:
            "Come autenticare una borsa firmata: la guida per tutte le maison",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/it/blog/chanel-classic-flap-guida-it",
          title:
            "Guida alla Chanel Classic Flap: misure, pellami, hardware e la questione 2.55",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_classic_flap_guide_ai_267580a053.jpg",
            width: 500,
            height: 281,
            alt: "Black quilted flap bag with gold chain on a velvet chair",
          },
        },
        {
          href: "/it/blog/come-curare-borse-in-pelle-it",
          title:
            "Come curare le borse in pelle: la routine che ne protegge il valore",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
    },
  },
  {
    key: "/bags/crossbody-bags",
    urls: {
      en: "/bags/crossbody-bags",
      nl: "/tassen/crossbody-tassen",
      de: "/taschen/crossbody-taschen",
      fr: "/sacs/sacs-bandouliere",
      es: "/bolsos/bolsos-cruzados",
      it: "/borse/borse-a-tracolla",
    },
    links: {
      en: [
        {
          href: "/blog/secondhand-designer-handbags-guide",
          title:
            "The Definitive Guide to Buying Secondhand Designer Handbags in Europe",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/blog/gucci-jackie-guide",
          title:
            "Gucci Jackie Guide: The 1961 Hobo, the 2020 Revival and Secondhand Buying",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_jackie_guide_ai_ee5b8b6043.jpg",
            width: 500,
            height: 281,
            alt: "Crescent-shaped tan leather hobo bag with a piston clasp",
          },
        },
        {
          href: "/blog/gucci-marmont-guide",
          title:
            "Gucci GG Marmont Guide: Sizes, the 2016 Design and Buying One Secondhand",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_marmont_guide_ai_8d6efa0d90.jpg",
            width: 500,
            height: 281,
            alt: "Chevron-quilted dusty pink shoulder bag with a chain strap",
          },
        },
        {
          href: "/blog/secondhand-gucci-bags-buying-guide",
          title:
            "Buying a Secondhand Gucci Bag: My Honest Guide to Canvas, Icons and Prices",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_gucci_bags_buying_guide_ai_81efe96a4e.jpg",
            width: 500,
            height: 281,
            alt: "Tan, burgundy and green leather handbags on a walnut sideboard",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/tweedehands-designertas-kopen-complete-gids-nl",
          title:
            "De complete gids voor het kopen van een tweedehands designertas in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/nl/blog/gucci-jackie-gids-nl",
          title:
            "Gucci Jackie gids: de hobo uit 1961, de revival van 2020 en tweedehands kopen",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_jackie_guide_ai_ee5b8b6043.jpg",
            width: 500,
            height: 281,
            alt: "Crescent-shaped tan leather hobo bag with a piston clasp",
          },
        },
        {
          href: "/nl/blog/gucci-gg-marmont-gids-nl",
          title:
            "Gucci GG Marmont gids: maten, het ontwerp uit 2016 en tweedehands kopen",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_marmont_guide_ai_8d6efa0d90.jpg",
            width: 500,
            height: 281,
            alt: "Chevron-quilted dusty pink shoulder bag with a chain strap",
          },
        },
        {
          href: "/nl/blog/tweedehands-gucci-tas-kopen-gids-nl",
          title:
            "Een tweedehands Gucci-tas kopen: mijn eerlijke gids voor canvas, iconen en prijzen",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_gucci_bags_buying_guide_ai_81efe96a4e.jpg",
            width: 500,
            height: 281,
            alt: "Tan, burgundy and green leather handbags on a walnut sideboard",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/gebrauchte-designertasche-kaufen-guide-de",
          title:
            "Der umfassende Guide zum Kauf gebrauchter Designertaschen in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/de/blog/gucci-jackie-guide-de",
          title:
            "Gucci Jackie Guide: die Hobo von 1961, das Revival von 2020 und der Gebrauchtkauf",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_jackie_guide_ai_ee5b8b6043.jpg",
            width: 500,
            height: 281,
            alt: "Crescent-shaped tan leather hobo bag with a piston clasp",
          },
        },
        {
          href: "/de/blog/gucci-gg-marmont-guide-de",
          title:
            "Gucci GG Marmont Guide: Größen, das Design von 2016 und der Gebrauchtkauf",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_marmont_guide_ai_8d6efa0d90.jpg",
            width: 500,
            height: 281,
            alt: "Chevron-quilted dusty pink shoulder bag with a chain strap",
          },
        },
        {
          href: "/de/blog/gebrauchte-gucci-tasche-kaufen-guide-de",
          title:
            "Eine gebrauchte Gucci Tasche kaufen: mein ehrlicher Guide zu Canvas, Ikonen und Preisen",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_gucci_bags_buying_guide_ai_81efe96a4e.jpg",
            width: 500,
            height: 281,
            alt: "Tan, burgundy and green leather handbags on a walnut sideboard",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/acheter-sac-de-luxe-seconde-main-guide-fr",
          title:
            "Le guide complet pour acheter un sac de luxe seconde main en Europe",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/fr/blog/gucci-jackie-guide-fr",
          title:
            "Guide du Gucci Jackie : le hobo de 1961, le retour de 2020 et la seconde main",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_jackie_guide_ai_ee5b8b6043.jpg",
            width: 500,
            height: 281,
            alt: "Crescent-shaped tan leather hobo bag with a piston clasp",
          },
        },
        {
          href: "/fr/blog/gucci-gg-marmont-guide-fr",
          title:
            "Guide du Gucci GG Marmont : tailles, le dessin de 2016 et l'achat en seconde main",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_marmont_guide_ai_8d6efa0d90.jpg",
            width: 500,
            height: 281,
            alt: "Chevron-quilted dusty pink shoulder bag with a chain strap",
          },
        },
        {
          href: "/fr/blog/acheter-sac-gucci-seconde-main-guide-fr",
          title:
            "Acheter un sac Gucci seconde main : mon guide honnête des toiles, des icônes et des prix",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_gucci_bags_buying_guide_ai_81efe96a4e.jpg",
            width: 500,
            height: 281,
            alt: "Tan, burgundy and green leather handbags on a walnut sideboard",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/comprar-bolso-de-lujo-segunda-mano-guia-es",
          title:
            "La guía definitiva para comprar un bolso de lujo de segunda mano en Europa",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/es/blog/gucci-jackie-guia-es",
          title:
            "Guía del Gucci Jackie: el hobo de 1961, el regreso de 2020 y la segunda mano",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_jackie_guide_ai_ee5b8b6043.jpg",
            width: 500,
            height: 281,
            alt: "Crescent-shaped tan leather hobo bag with a piston clasp",
          },
        },
        {
          href: "/es/blog/gucci-gg-marmont-guia-es",
          title:
            "Guía del Gucci GG Marmont: tallas, el diseño de 2016 y la compra de segunda mano",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_marmont_guide_ai_8d6efa0d90.jpg",
            width: 500,
            height: 281,
            alt: "Chevron-quilted dusty pink shoulder bag with a chain strap",
          },
        },
        {
          href: "/es/blog/comprar-bolso-gucci-segunda-mano-guia-es",
          title:
            "Comprar un bolso Gucci de segunda mano: mi guía honesta de lonas, iconos y precios",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_gucci_bags_buying_guide_ai_81efe96a4e.jpg",
            width: 500,
            height: 281,
            alt: "Tan, burgundy and green leather handbags on a walnut sideboard",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/comprare-borsa-firmata-usata-guida-it",
          title:
            "La guida definitiva per comprare una borsa firmata usata in Europa",
          readingMinutes: 15,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/it/blog/gucci-jackie-guida-it",
          title:
            "Guida alla Gucci Jackie: l'hobo del 1961, il ritorno del 2020 e l'usato",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_jackie_guide_ai_ee5b8b6043.jpg",
            width: 500,
            height: 281,
            alt: "Crescent-shaped tan leather hobo bag with a piston clasp",
          },
        },
        {
          href: "/it/blog/gucci-gg-marmont-guida-it",
          title:
            "Guida alla Gucci GG Marmont: misure, il disegno del 2016 e l'acquisto usato",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_marmont_guide_ai_8d6efa0d90.jpg",
            width: 500,
            height: 281,
            alt: "Chevron-quilted dusty pink shoulder bag with a chain strap",
          },
        },
        {
          href: "/it/blog/comprare-borsa-gucci-usata-guida-it",
          title:
            "Comprare una borsa Gucci usata: la mia guida onesta a tele, icone e prezzi",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_gucci_bags_buying_guide_ai_81efe96a4e.jpg",
            width: 500,
            height: 281,
            alt: "Tan, burgundy and green leather handbags on a walnut sideboard",
          },
        },
      ],
    },
  },
  {
    key: "/bags/laptop-bags",
    urls: {
      en: "/bags/laptop-bags",
      nl: "/tassen/laptoptassen",
      de: "/taschen/laptoptaschen",
      fr: "/sacs/sacoches-pour-ordinateur",
      es: "/bolsos/bolsas-para-portatil",
      it: "/borse/borse-per-laptop",
    },
    links: {
      en: [
        {
          href: "/blog/secondhand-designer-handbags-guide",
          title:
            "The Definitive Guide to Buying Secondhand Designer Handbags in Europe",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/blog/louis-vuitton-neverfull-guide",
          title:
            "Louis Vuitton Neverfull Guide: PM, MM, GM and Why It Sells So Fast",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/blog/louis-vuitton-speedy-guide",
          title:
            "Louis Vuitton Speedy Guide: Sizes 25 to 40, History and Buying Secondhand",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/blog/secondhand-louis-vuitton-bags-buying-guide",
          title:
            "Buying a Secondhand Louis Vuitton Bag: My Honest Guide to Models and Canvas",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/tweedehands-designertas-kopen-complete-gids-nl",
          title:
            "De complete gids voor het kopen van een tweedehands designertas in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/nl/blog/louis-vuitton-neverfull-gids-nl",
          title:
            "Louis Vuitton Neverfull gids: PM, MM, GM en waarom hij zo snel verkocht is",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/nl/blog/louis-vuitton-speedy-gids-nl",
          title:
            "Louis Vuitton Speedy gids: maten 25 tot 40, geschiedenis en tweedehands kopen",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/nl/blog/tweedehands-louis-vuitton-tas-kopen-gids-nl",
          title:
            "Een tweedehands Louis Vuitton-tas kopen: mijn eerlijke gids voor modellen en canvas",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/gebrauchte-designertasche-kaufen-guide-de",
          title:
            "Der umfassende Guide zum Kauf gebrauchter Designertaschen in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/de/blog/louis-vuitton-neverfull-guide-de",
          title:
            "Louis Vuitton Neverfull Guide: PM, MM, GM und warum sie so schnell weg ist",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/de/blog/louis-vuitton-speedy-guide-de",
          title:
            "Louis Vuitton Speedy Guide: Größen 25 bis 40, Geschichte und Gebrauchtkauf",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/de/blog/gebrauchte-louis-vuitton-tasche-kaufen-guide-de",
          title:
            "Eine gebrauchte Louis Vuitton Tasche kaufen: mein ehrlicher Guide zu Modellen und Canvas",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/acheter-sac-de-luxe-seconde-main-guide-fr",
          title:
            "Le guide complet pour acheter un sac de luxe seconde main en Europe",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/fr/blog/louis-vuitton-neverfull-guide-fr",
          title:
            "Guide du Louis Vuitton Neverfull : PM, MM, GM et pourquoi il part si vite",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/fr/blog/louis-vuitton-speedy-guide-fr",
          title:
            "Guide du Louis Vuitton Speedy : tailles 25 à 40, histoire et achat en seconde main",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/fr/blog/acheter-sac-louis-vuitton-seconde-main-guide-fr",
          title:
            "Acheter un sac Louis Vuitton seconde main : mon guide honnête des modèles et des toiles",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/comprar-bolso-de-lujo-segunda-mano-guia-es",
          title:
            "La guía definitiva para comprar un bolso de lujo de segunda mano en Europa",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/es/blog/louis-vuitton-neverfull-guia-es",
          title:
            "Guía del Louis Vuitton Neverfull: PM, MM, GM y por qué se vende tan rápido",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/es/blog/louis-vuitton-speedy-guia-es",
          title:
            "Guía del Louis Vuitton Speedy: tallas de la 25 a la 40, historia y compra de segunda mano",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/es/blog/comprar-bolso-louis-vuitton-segunda-mano-guia-es",
          title:
            "Comprar un bolso Louis Vuitton de segunda mano: mi guía honesta de modelos y lonas",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/comprare-borsa-firmata-usata-guida-it",
          title:
            "La guida definitiva per comprare una borsa firmata usata in Europa",
          readingMinutes: 15,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/it/blog/louis-vuitton-neverfull-guida-it",
          title:
            "Guida alla Louis Vuitton Neverfull: PM, MM, GM e perché si vende così in fretta",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/it/blog/louis-vuitton-speedy-guida-it",
          title:
            "Guida alla Louis Vuitton Speedy: misure dalla 25 alla 40, storia e acquisto usato",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/it/blog/comprare-borsa-louis-vuitton-usata-guida-it",
          title:
            "Comprare una borsa Louis Vuitton usata: la mia guida onesta a modelli e tele",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
      ],
    },
  },
  {
    key: "/bags/other-bags",
    urls: {
      en: "/bags/other-bags",
      nl: "/tassen/overige-tassen",
      de: "/taschen/andere-taschen",
      fr: "/sacs/autres-sacs",
      es: "/bolsos/otros-bolsos",
      it: "/borse/altre-borse",
    },
    links: {
      en: [
        {
          href: "/blog/secondhand-designer-handbags-guide",
          title:
            "The Definitive Guide to Buying Secondhand Designer Handbags in Europe",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/blog/handbag-authentication-guide",
          title:
            "How to Authenticate a Designer Handbag: The Cross-Brand Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/blog/how-to-care-for-leather-handbags",
          title:
            "How to Care for Leather Handbags: The Routine That Protects Their Value",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/tweedehands-designertas-kopen-complete-gids-nl",
          title:
            "De complete gids voor het kopen van een tweedehands designertas in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/nl/blog/designertas-authenticiteit-controleren-gids-nl",
          title:
            "Hoe herken je een echte designertas? De gids voor alle merken",
          readingMinutes: 12,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/nl/blog/leren-tassen-onderhouden-nl",
          title:
            "Leren tassen onderhouden: de routine die hun waarde beschermt",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/gebrauchte-designertasche-kaufen-guide-de",
          title:
            "Der umfassende Guide zum Kauf gebrauchter Designertaschen in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/de/blog/designertasche-echtheit-pruefen-guide-de",
          title:
            "Designertasche auf Echtheit prüfen: der markenübergreifende Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/de/blog/ledertaschen-pflegen-de",
          title: "Ledertaschen pflegen: die Routine, die ihren Wert schützt",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/acheter-sac-de-luxe-seconde-main-guide-fr",
          title:
            "Le guide complet pour acheter un sac de luxe seconde main en Europe",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/fr/blog/authentifier-un-sac-de-luxe-guide-fr",
          title: "Comment authentifier un sac de luxe : le guide multimarques",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/fr/blog/entretien-sac-en-cuir-fr",
          title: "Entretenir un sac en cuir : la routine qui protège sa valeur",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/comprar-bolso-de-lujo-segunda-mano-guia-es",
          title:
            "La guía definitiva para comprar un bolso de lujo de segunda mano en Europa",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/es/blog/autenticar-un-bolso-de-lujo-guia-es",
          title:
            "Cómo autenticar un bolso de lujo: la guía para todas las marcas",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/es/blog/cuidar-bolsos-de-piel-es",
          title:
            "Cómo cuidar los bolsos de piel: la rutina que protege su valor",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/comprare-borsa-firmata-usata-guida-it",
          title:
            "La guida definitiva per comprare una borsa firmata usata in Europa",
          readingMinutes: 15,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/it/blog/autenticare-una-borsa-firmata-guida-it",
          title:
            "Come autenticare una borsa firmata: la guida per tutte le maison",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/it/blog/come-curare-borse-in-pelle-it",
          title:
            "Come curare le borse in pelle: la routine che ne protegge il valore",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
    },
  },
  {
    key: "/bags/shoulder-bags",
    urls: {
      en: "/bags/shoulder-bags",
      nl: "/tassen/schoudertassen",
      de: "/taschen/schultertaschen",
      fr: "/sacs/sacs-a-bandouliere",
      es: "/bolsos/bolsos-de-hombro",
      it: "/borse/borse-a-spalla",
    },
    links: {
      en: [
        {
          href: "/blog/secondhand-designer-handbags-guide",
          title:
            "The Definitive Guide to Buying Secondhand Designer Handbags in Europe",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/blog/chanel-classic-flap-guide",
          title:
            "Chanel Classic Flap Guide: Sizes, Leathers, Hardware and the 2.55 Question",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_classic_flap_guide_ai_267580a053.jpg",
            width: 500,
            height: 281,
            alt: "Black quilted flap bag with gold chain on a velvet chair",
          },
        },
        {
          href: "/blog/chanel-255-guide",
          title: "The Chanel 2.55: Why the February 1955 Original Still Wins",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_255_guide_ai_ee0b559a1b.jpg",
            width: 500,
            height: 281,
            alt: "Burgundy quilted shoulder bag with chain strap on a marble table",
          },
        },
        {
          href: "/blog/secondhand-chanel-bags-buying-guide",
          title:
            "Buying a Secondhand Chanel Bag: My Honest Guide to Models, Sizes and Prices",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_chanel_bags_buying_guide_ai_3abc23385f.jpg",
            width: 500,
            height: 281,
            alt: "Quilted cream leather flap bag with gold chain on draped fabric",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/tweedehands-designertas-kopen-complete-gids-nl",
          title:
            "De complete gids voor het kopen van een tweedehands designertas in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/nl/blog/chanel-classic-flap-gids-nl",
          title:
            "Chanel Classic Flap gids: maten, leersoorten, hardware en de 2.55-vraag",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_classic_flap_guide_ai_267580a053.jpg",
            width: 500,
            height: 281,
            alt: "Black quilted flap bag with gold chain on a velvet chair",
          },
        },
        {
          href: "/nl/blog/chanel-2-55-gids-nl",
          title:
            "De Chanel 2.55: waarom het origineel uit februari 1955 nog altijd wint",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_255_guide_ai_ee0b559a1b.jpg",
            width: 500,
            height: 281,
            alt: "Burgundy quilted shoulder bag with chain strap on a marble table",
          },
        },
        {
          href: "/nl/blog/tweedehands-chanel-tas-kopen-gids-nl",
          title:
            "Een tweedehands Chanel-tas kopen: mijn eerlijke gids voor modellen, maten en prijzen",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_chanel_bags_buying_guide_ai_3abc23385f.jpg",
            width: 500,
            height: 281,
            alt: "Quilted cream leather flap bag with gold chain on draped fabric",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/gebrauchte-designertasche-kaufen-guide-de",
          title:
            "Der umfassende Guide zum Kauf gebrauchter Designertaschen in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/de/blog/chanel-classic-flap-guide-de",
          title:
            "Chanel Classic Flap Guide: Größen, Leder, Hardware und die 2.55-Frage",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_classic_flap_guide_ai_267580a053.jpg",
            width: 500,
            height: 281,
            alt: "Black quilted flap bag with gold chain on a velvet chair",
          },
        },
        {
          href: "/de/blog/chanel-2-55-guide-de",
          title:
            "Die Chanel 2.55: warum das Original vom Februar 1955 immer noch gewinnt",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_255_guide_ai_ee0b559a1b.jpg",
            width: 500,
            height: 281,
            alt: "Burgundy quilted shoulder bag with chain strap on a marble table",
          },
        },
        {
          href: "/de/blog/gebrauchte-chanel-tasche-kaufen-guide-de",
          title:
            "Eine gebrauchte Chanel-Tasche kaufen: mein ehrlicher Guide zu Modellen, Größen und Preisen",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_chanel_bags_buying_guide_ai_3abc23385f.jpg",
            width: 500,
            height: 281,
            alt: "Quilted cream leather flap bag with gold chain on draped fabric",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/acheter-sac-de-luxe-seconde-main-guide-fr",
          title:
            "Le guide complet pour acheter un sac de luxe seconde main en Europe",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/fr/blog/chanel-classic-flap-guide-fr",
          title:
            "Guide du Chanel Classic Flap : tailles, cuirs, métal et la question du 2.55",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_classic_flap_guide_ai_267580a053.jpg",
            width: 500,
            height: 281,
            alt: "Black quilted flap bag with gold chain on a velvet chair",
          },
        },
        {
          href: "/fr/blog/chanel-2-55-guide-fr",
          title:
            "Le Chanel 2.55 : pourquoi l'original de février 1955 reste indépassable",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_255_guide_ai_ee0b559a1b.jpg",
            width: 500,
            height: 281,
            alt: "Burgundy quilted shoulder bag with chain strap on a marble table",
          },
        },
        {
          href: "/fr/blog/acheter-sac-chanel-seconde-main-guide-fr",
          title:
            "Acheter un sac Chanel seconde main : mon guide honnête des modèles, tailles et prix",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_chanel_bags_buying_guide_ai_3abc23385f.jpg",
            width: 500,
            height: 281,
            alt: "Quilted cream leather flap bag with gold chain on draped fabric",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/comprar-bolso-de-lujo-segunda-mano-guia-es",
          title:
            "La guía definitiva para comprar un bolso de lujo de segunda mano en Europa",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/es/blog/chanel-classic-flap-guia-es",
          title:
            "Guía del Chanel Classic Flap: tallas, pieles, herrajes y la cuestión del 2.55",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_classic_flap_guide_ai_267580a053.jpg",
            width: 500,
            height: 281,
            alt: "Black quilted flap bag with gold chain on a velvet chair",
          },
        },
        {
          href: "/es/blog/chanel-2-55-guia-es",
          title:
            "El Chanel 2.55: por qué el original de febrero de 1955 sigue ganando",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_255_guide_ai_ee0b559a1b.jpg",
            width: 500,
            height: 281,
            alt: "Burgundy quilted shoulder bag with chain strap on a marble table",
          },
        },
        {
          href: "/es/blog/comprar-bolso-chanel-segunda-mano-guia-es",
          title:
            "Comprar un bolso Chanel de segunda mano: mi guía honesta de modelos, tallas y precios",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_chanel_bags_buying_guide_ai_3abc23385f.jpg",
            width: 500,
            height: 281,
            alt: "Quilted cream leather flap bag with gold chain on draped fabric",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/comprare-borsa-firmata-usata-guida-it",
          title:
            "La guida definitiva per comprare una borsa firmata usata in Europa",
          readingMinutes: 15,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/it/blog/chanel-classic-flap-guida-it",
          title:
            "Guida alla Chanel Classic Flap: misure, pellami, hardware e la questione 2.55",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_classic_flap_guide_ai_267580a053.jpg",
            width: 500,
            height: 281,
            alt: "Black quilted flap bag with gold chain on a velvet chair",
          },
        },
        {
          href: "/it/blog/chanel-2-55-guida-it",
          title:
            "La Chanel 2.55: perché l'originale del febbraio 1955 vince ancora",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_255_guide_ai_ee0b559a1b.jpg",
            width: 500,
            height: 281,
            alt: "Burgundy quilted shoulder bag with chain strap on a marble table",
          },
        },
        {
          href: "/it/blog/comprare-borsa-chanel-usata-guida-it",
          title:
            "Comprare una borsa Chanel usata: la mia guida onesta a modelli, misure e prezzi",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_chanel_bags_buying_guide_ai_3abc23385f.jpg",
            width: 500,
            height: 281,
            alt: "Quilted cream leather flap bag with gold chain on draped fabric",
          },
        },
      ],
    },
  },
  {
    key: "/bags/top-handle-bags",
    urls: {
      en: "/bags/top-handle-bags",
      nl: "/tassen/top-handle-tassen",
      de: "/taschen/henkeltaschen",
      fr: "/sacs/sacs-a-main",
      es: "/bolsos/bolsos-de-mano",
      it: "/borse/borse-a-mano",
    },
    links: {
      en: [
        {
          href: "/blog/hermes-kelly-guide",
          title: "Hermes Kelly Guide: History, Sizes, Sellier vs Retourne",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_kelly_guide_ai_2ccb45932c.jpg",
            width: 500,
            height: 281,
            alt: "Trapezoid top-handle bag in deep green leather on a cafe table",
          },
        },
        {
          href: "/blog/dior-lady-dior-guide",
          title:
            "Lady Dior Guide: The Bag Named for Princess Diana, Sizes and Secondhand Buying",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_lady_dior_guide_ai_ea94d74772.jpg",
            width: 500,
            height: 281,
            alt: "Structured cream quilted top-handle bag with gold-tone charms",
          },
        },
        {
          href: "/blog/hermes-birkin-guide",
          title:
            "Hermes Birkin Guide: Sizes, Leathers and Buying One Secondhand",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_birkin_guide_ai_6c4086b316.jpg",
            width: 500,
            height: 281,
            alt: "Structured cognac leather handbag on a stone windowsill",
          },
        },
        {
          href: "/blog/secondhand-hermes-bags-buying-guide",
          title: "How to Buy a Secondhand Hermes Bag: My Honest Guide",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_hermes_bags_buying_guide_ai_5f5a8a2ca4.jpg",
            width: 500,
            height: 281,
            alt: "Structured tan top-handle bag on a wooden shelf with dried grasses",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/hermes-kelly-gids-nl",
          title:
            "Hermes Kelly gids: geschiedenis, maten, sellier versus retourné",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_kelly_guide_ai_2ccb45932c.jpg",
            width: 500,
            height: 281,
            alt: "Trapezoid top-handle bag in deep green leather on a cafe table",
          },
        },
        {
          href: "/nl/blog/dior-lady-dior-gids-nl",
          title:
            "Lady Dior gids: de tas vernoemd naar prinses Diana, maten en tweedehands kopen",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_lady_dior_guide_ai_ea94d74772.jpg",
            width: 500,
            height: 281,
            alt: "Structured cream quilted top-handle bag with gold-tone charms",
          },
        },
        {
          href: "/nl/blog/hermes-birkin-gids-nl",
          title: "Hermes Birkin gids: maten, leersoorten en tweedehands kopen",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_birkin_guide_ai_6c4086b316.jpg",
            width: 500,
            height: 281,
            alt: "Structured cognac leather handbag on a stone windowsill",
          },
        },
        {
          href: "/nl/blog/tweedehands-hermes-tas-kopen-gids-nl",
          title: "Een tweedehands Hermes-tas kopen: mijn eerlijke gids",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_hermes_bags_buying_guide_ai_5f5a8a2ca4.jpg",
            width: 500,
            height: 281,
            alt: "Structured tan top-handle bag on a wooden shelf with dried grasses",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/hermes-kelly-guide-de",
          title:
            "Hermes Kelly Guide: Geschichte, Größen, Sellier versus Retourné",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_kelly_guide_ai_2ccb45932c.jpg",
            width: 500,
            height: 281,
            alt: "Trapezoid top-handle bag in deep green leather on a cafe table",
          },
        },
        {
          href: "/de/blog/dior-lady-dior-guide-de",
          title:
            "Lady Dior Guide: die Tasche, die Prinzessin Diana ihren Namen gab, Größen und Gebrauchtkauf",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_lady_dior_guide_ai_ea94d74772.jpg",
            width: 500,
            height: 281,
            alt: "Structured cream quilted top-handle bag with gold-tone charms",
          },
        },
        {
          href: "/de/blog/hermes-birkin-guide-de",
          title: "Hermes Birkin Guide: Größen, Leder und der Gebrauchtkauf",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_birkin_guide_ai_6c4086b316.jpg",
            width: 500,
            height: 281,
            alt: "Structured cognac leather handbag on a stone windowsill",
          },
        },
        {
          href: "/de/blog/gebrauchte-hermes-tasche-kaufen-guide-de",
          title: "Eine gebrauchte Hermes-Tasche kaufen: mein ehrlicher Guide",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_hermes_bags_buying_guide_ai_5f5a8a2ca4.jpg",
            width: 500,
            height: 281,
            alt: "Structured tan top-handle bag on a wooden shelf with dried grasses",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/hermes-kelly-guide-fr",
          title:
            "Guide du Hermes Kelly : histoire, tailles, sellier ou retourné",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_kelly_guide_ai_2ccb45932c.jpg",
            width: 500,
            height: 281,
            alt: "Trapezoid top-handle bag in deep green leather on a cafe table",
          },
        },
        {
          href: "/fr/blog/dior-lady-dior-guide-fr",
          title:
            "Guide du Lady Dior : le sac qui doit son nom à la princesse Diana, tailles et seconde main",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_lady_dior_guide_ai_ea94d74772.jpg",
            width: 500,
            height: 281,
            alt: "Structured cream quilted top-handle bag with gold-tone charms",
          },
        },
        {
          href: "/fr/blog/hermes-birkin-guide-fr",
          title:
            "Guide du Hermes Birkin : tailles, cuirs et achat en seconde main",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_birkin_guide_ai_6c4086b316.jpg",
            width: 500,
            height: 281,
            alt: "Structured cognac leather handbag on a stone windowsill",
          },
        },
        {
          href: "/fr/blog/acheter-sac-hermes-seconde-main-guide-fr",
          title: "Acheter un sac Hermes seconde main : mon guide honnête",
          readingMinutes: 10,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_hermes_bags_buying_guide_ai_5f5a8a2ca4.jpg",
            width: 500,
            height: 281,
            alt: "Structured tan top-handle bag on a wooden shelf with dried grasses",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/hermes-kelly-guia-es",
          title:
            "Guía del Hermes Kelly: historia, tallas, sellier frente a retourné",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_kelly_guide_ai_2ccb45932c.jpg",
            width: 500,
            height: 281,
            alt: "Trapezoid top-handle bag in deep green leather on a cafe table",
          },
        },
        {
          href: "/es/blog/dior-lady-dior-guia-es",
          title:
            "Guía del Lady Dior: el bolso que debe su nombre a la princesa Diana, tallas y segunda mano",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_lady_dior_guide_ai_ea94d74772.jpg",
            width: 500,
            height: 281,
            alt: "Structured cream quilted top-handle bag with gold-tone charms",
          },
        },
        {
          href: "/es/blog/hermes-birkin-guia-es",
          title:
            "Guía del Hermes Birkin: tallas, pieles y cómo comprarlo de segunda mano",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_birkin_guide_ai_6c4086b316.jpg",
            width: 500,
            height: 281,
            alt: "Structured cognac leather handbag on a stone windowsill",
          },
        },
        {
          href: "/es/blog/comprar-bolso-hermes-segunda-mano-guia-es",
          title:
            "Cómo comprar un bolso Hermes de segunda mano: mi guía honesta",
          readingMinutes: 10,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_hermes_bags_buying_guide_ai_5f5a8a2ca4.jpg",
            width: 500,
            height: 281,
            alt: "Structured tan top-handle bag on a wooden shelf with dried grasses",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/hermes-kelly-guida-it",
          title: "Guida alla Hermes Kelly: storia, misure, sellier o retourné",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_kelly_guide_ai_2ccb45932c.jpg",
            width: 500,
            height: 281,
            alt: "Trapezoid top-handle bag in deep green leather on a cafe table",
          },
        },
        {
          href: "/it/blog/dior-lady-dior-guida-it",
          title:
            "Guida alla Lady Dior: la borsa che deve il nome alla principessa Diana, misure e usato",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_lady_dior_guide_ai_ea94d74772.jpg",
            width: 500,
            height: 281,
            alt: "Structured cream quilted top-handle bag with gold-tone charms",
          },
        },
        {
          href: "/it/blog/hermes-birkin-guida-it",
          title:
            "Guida alla Hermes Birkin: misure, pellami e come comprarla usata",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_birkin_guide_ai_6c4086b316.jpg",
            width: 500,
            height: 281,
            alt: "Structured cognac leather handbag on a stone windowsill",
          },
        },
        {
          href: "/it/blog/comprare-borsa-hermes-usata-guida-it",
          title: "Come comprare una borsa Hermes usata: la mia guida onesta",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_hermes_bags_buying_guide_ai_5f5a8a2ca4.jpg",
            width: 500,
            height: 281,
            alt: "Structured tan top-handle bag on a wooden shelf with dried grasses",
          },
        },
      ],
    },
  },
  {
    key: "/bags/tote-bags",
    urls: {
      en: "/bags/tote-bags",
      nl: "/tassen/tote-tassen",
      de: "/taschen/tote-taschen",
      fr: "/sacs/sacs-cabas",
      es: "/bolsos/bolsos-tote",
      it: "/borse/borse-tote",
    },
    links: {
      en: [
        {
          href: "/blog/louis-vuitton-neverfull-guide",
          title:
            "Louis Vuitton Neverfull Guide: PM, MM, GM and Why It Sells So Fast",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/blog/louis-vuitton-speedy-guide",
          title:
            "Louis Vuitton Speedy Guide: Sizes 25 to 40, History and Buying Secondhand",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/blog/secondhand-celine-saint-laurent-bags-guide",
          title:
            "Secondhand Celine and Saint Laurent: My Guide to the Paris Minimalists",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_celine_saint_laurent_bags_guide_ai_121bc8e0fb.jpg",
            width: 500,
            height: 281,
            alt: "Boxy tan top-handle bag and quilted black chain bag on oak",
          },
        },
        {
          href: "/blog/secondhand-louis-vuitton-bags-buying-guide",
          title:
            "Buying a Secondhand Louis Vuitton Bag: My Honest Guide to Models and Canvas",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/louis-vuitton-neverfull-gids-nl",
          title:
            "Louis Vuitton Neverfull gids: PM, MM, GM en waarom hij zo snel verkocht is",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/nl/blog/louis-vuitton-speedy-gids-nl",
          title:
            "Louis Vuitton Speedy gids: maten 25 tot 40, geschiedenis en tweedehands kopen",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/nl/blog/tweedehands-celine-saint-laurent-tas-kopen-nl",
          title:
            "Tweedehands Celine en Saint Laurent: mijn gids voor de Parijse minimalisten",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_celine_saint_laurent_bags_guide_ai_121bc8e0fb.jpg",
            width: 500,
            height: 281,
            alt: "Boxy tan top-handle bag and quilted black chain bag on oak",
          },
        },
        {
          href: "/nl/blog/tweedehands-louis-vuitton-tas-kopen-gids-nl",
          title:
            "Een tweedehands Louis Vuitton-tas kopen: mijn eerlijke gids voor modellen en canvas",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/louis-vuitton-neverfull-guide-de",
          title:
            "Louis Vuitton Neverfull Guide: PM, MM, GM und warum sie so schnell weg ist",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/de/blog/louis-vuitton-speedy-guide-de",
          title:
            "Louis Vuitton Speedy Guide: Größen 25 bis 40, Geschichte und Gebrauchtkauf",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/de/blog/gebrauchte-celine-saint-laurent-tasche-kaufen-de",
          title:
            "Celine und Saint Laurent gebraucht: mein Guide zu den Pariser Minimalisten",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_celine_saint_laurent_bags_guide_ai_121bc8e0fb.jpg",
            width: 500,
            height: 281,
            alt: "Boxy tan top-handle bag and quilted black chain bag on oak",
          },
        },
        {
          href: "/de/blog/gebrauchte-louis-vuitton-tasche-kaufen-guide-de",
          title:
            "Eine gebrauchte Louis Vuitton Tasche kaufen: mein ehrlicher Guide zu Modellen und Canvas",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/louis-vuitton-neverfull-guide-fr",
          title:
            "Guide du Louis Vuitton Neverfull : PM, MM, GM et pourquoi il part si vite",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/fr/blog/louis-vuitton-speedy-guide-fr",
          title:
            "Guide du Louis Vuitton Speedy : tailles 25 à 40, histoire et achat en seconde main",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/fr/blog/celine-saint-laurent-seconde-main-guide-fr",
          title:
            "Celine et Saint Laurent seconde main : mon guide des minimalistes parisiens",
          readingMinutes: 10,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_celine_saint_laurent_bags_guide_ai_121bc8e0fb.jpg",
            width: 500,
            height: 281,
            alt: "Boxy tan top-handle bag and quilted black chain bag on oak",
          },
        },
        {
          href: "/fr/blog/acheter-sac-louis-vuitton-seconde-main-guide-fr",
          title:
            "Acheter un sac Louis Vuitton seconde main : mon guide honnête des modèles et des toiles",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/louis-vuitton-neverfull-guia-es",
          title:
            "Guía del Louis Vuitton Neverfull: PM, MM, GM y por qué se vende tan rápido",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/es/blog/louis-vuitton-speedy-guia-es",
          title:
            "Guía del Louis Vuitton Speedy: tallas de la 25 a la 40, historia y compra de segunda mano",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/es/blog/celine-saint-laurent-segunda-mano-guia-es",
          title:
            "Celine y Saint Laurent de segunda mano: mi guía de las minimalistas de París",
          readingMinutes: 10,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_celine_saint_laurent_bags_guide_ai_121bc8e0fb.jpg",
            width: 500,
            height: 281,
            alt: "Boxy tan top-handle bag and quilted black chain bag on oak",
          },
        },
        {
          href: "/es/blog/comprar-bolso-louis-vuitton-segunda-mano-guia-es",
          title:
            "Comprar un bolso Louis Vuitton de segunda mano: mi guía honesta de modelos y lonas",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/louis-vuitton-neverfull-guida-it",
          title:
            "Guida alla Louis Vuitton Neverfull: PM, MM, GM e perché si vende così in fretta",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/it/blog/louis-vuitton-speedy-guida-it",
          title:
            "Guida alla Louis Vuitton Speedy: misure dalla 25 alla 40, storia e acquisto usato",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/it/blog/celine-saint-laurent-usate-guida-it",
          title:
            "Celine e Saint Laurent usate: la mia guida alle minimaliste di Parigi",
          readingMinutes: 10,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_celine_saint_laurent_bags_guide_ai_121bc8e0fb.jpg",
            width: 500,
            height: 281,
            alt: "Boxy tan top-handle bag and quilted black chain bag on oak",
          },
        },
        {
          href: "/it/blog/comprare-borsa-louis-vuitton-usata-guida-it",
          title:
            "Comprare una borsa Louis Vuitton usata: la mia guida onesta a modelli e tele",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
      ],
    },
  },
  {
    key: "/bags/travel-bags",
    urls: {
      en: "/bags/travel-bags",
      nl: "/tassen/reistassen",
      de: "/taschen/reisetaschen",
      fr: "/sacs/sacs-de-voyage",
      es: "/bolsos/bolsos-de-viaje",
      it: "/borse/borse-da-viaggio",
    },
    links: {
      en: [
        {
          href: "/blog/louis-vuitton-speedy-guide",
          title:
            "Louis Vuitton Speedy Guide: Sizes 25 to 40, History and Buying Secondhand",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/blog/louis-vuitton-neverfull-guide",
          title:
            "Louis Vuitton Neverfull Guide: PM, MM, GM and Why It Sells So Fast",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/blog/secondhand-louis-vuitton-bags-buying-guide",
          title:
            "Buying a Secondhand Louis Vuitton Bag: My Honest Guide to Models and Canvas",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
        {
          href: "/blog/secondhand-designer-handbags-guide",
          title:
            "The Definitive Guide to Buying Secondhand Designer Handbags in Europe",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/louis-vuitton-speedy-gids-nl",
          title:
            "Louis Vuitton Speedy gids: maten 25 tot 40, geschiedenis en tweedehands kopen",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/nl/blog/louis-vuitton-neverfull-gids-nl",
          title:
            "Louis Vuitton Neverfull gids: PM, MM, GM en waarom hij zo snel verkocht is",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/nl/blog/tweedehands-louis-vuitton-tas-kopen-gids-nl",
          title:
            "Een tweedehands Louis Vuitton-tas kopen: mijn eerlijke gids voor modellen en canvas",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
        {
          href: "/nl/blog/tweedehands-designertas-kopen-complete-gids-nl",
          title:
            "De complete gids voor het kopen van een tweedehands designertas in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/louis-vuitton-speedy-guide-de",
          title:
            "Louis Vuitton Speedy Guide: Größen 25 bis 40, Geschichte und Gebrauchtkauf",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/de/blog/louis-vuitton-neverfull-guide-de",
          title:
            "Louis Vuitton Neverfull Guide: PM, MM, GM und warum sie so schnell weg ist",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/de/blog/gebrauchte-louis-vuitton-tasche-kaufen-guide-de",
          title:
            "Eine gebrauchte Louis Vuitton Tasche kaufen: mein ehrlicher Guide zu Modellen und Canvas",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
        {
          href: "/de/blog/gebrauchte-designertasche-kaufen-guide-de",
          title:
            "Der umfassende Guide zum Kauf gebrauchter Designertaschen in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/louis-vuitton-speedy-guide-fr",
          title:
            "Guide du Louis Vuitton Speedy : tailles 25 à 40, histoire et achat en seconde main",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/fr/blog/louis-vuitton-neverfull-guide-fr",
          title:
            "Guide du Louis Vuitton Neverfull : PM, MM, GM et pourquoi il part si vite",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/fr/blog/acheter-sac-louis-vuitton-seconde-main-guide-fr",
          title:
            "Acheter un sac Louis Vuitton seconde main : mon guide honnête des modèles et des toiles",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
        {
          href: "/fr/blog/acheter-sac-de-luxe-seconde-main-guide-fr",
          title:
            "Le guide complet pour acheter un sac de luxe seconde main en Europe",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/louis-vuitton-speedy-guia-es",
          title:
            "Guía del Louis Vuitton Speedy: tallas de la 25 a la 40, historia y compra de segunda mano",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/es/blog/louis-vuitton-neverfull-guia-es",
          title:
            "Guía del Louis Vuitton Neverfull: PM, MM, GM y por qué se vende tan rápido",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/es/blog/comprar-bolso-louis-vuitton-segunda-mano-guia-es",
          title:
            "Comprar un bolso Louis Vuitton de segunda mano: mi guía honesta de modelos y lonas",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
        {
          href: "/es/blog/comprar-bolso-de-lujo-segunda-mano-guia-es",
          title:
            "La guía definitiva para comprar un bolso de lujo de segunda mano en Europa",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/louis-vuitton-speedy-guida-it",
          title:
            "Guida alla Louis Vuitton Speedy: misure dalla 25 alla 40, storia e acquisto usato",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/it/blog/louis-vuitton-neverfull-guida-it",
          title:
            "Guida alla Louis Vuitton Neverfull: PM, MM, GM e perché si vende così in fretta",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/it/blog/comprare-borsa-louis-vuitton-usata-guida-it",
          title:
            "Comprare una borsa Louis Vuitton usata: la mia guida onesta a modelli e tele",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
        {
          href: "/it/blog/comprare-borsa-firmata-usata-guida-it",
          title:
            "La guida definitiva per comprare una borsa firmata usata in Europa",
          readingMinutes: 15,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
      ],
    },
  },
  {
    key: "/bags/wallets-small-leather-goods",
    urls: {
      en: "/bags/wallets-small-leather-goods",
      nl: "/tassen/portemonnees-klein-lederwaren",
      de: "/taschen/geldbeutel-kleinlederwaren",
      fr: "/sacs/portefeuilles-petite-maroquinerie",
      es: "/bolsos/carteras-y-pequena-marroquineria",
      it: "/borse/portafogli-piccola-pelletteria",
    },
    links: {
      en: [
        {
          href: "/blog/secondhand-designer-handbags-guide",
          title:
            "The Definitive Guide to Buying Secondhand Designer Handbags in Europe",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/blog/handbag-authentication-guide",
          title:
            "How to Authenticate a Designer Handbag: The Cross-Brand Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/blog/how-to-care-for-leather-handbags",
          title:
            "How to Care for Leather Handbags: The Routine That Protects Their Value",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/tweedehands-designertas-kopen-complete-gids-nl",
          title:
            "De complete gids voor het kopen van een tweedehands designertas in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/nl/blog/designertas-authenticiteit-controleren-gids-nl",
          title:
            "Hoe herken je een echte designertas? De gids voor alle merken",
          readingMinutes: 12,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/nl/blog/leren-tassen-onderhouden-nl",
          title:
            "Leren tassen onderhouden: de routine die hun waarde beschermt",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/gebrauchte-designertasche-kaufen-guide-de",
          title:
            "Der umfassende Guide zum Kauf gebrauchter Designertaschen in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/de/blog/designertasche-echtheit-pruefen-guide-de",
          title:
            "Designertasche auf Echtheit prüfen: der markenübergreifende Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/de/blog/ledertaschen-pflegen-de",
          title: "Ledertaschen pflegen: die Routine, die ihren Wert schützt",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/acheter-sac-de-luxe-seconde-main-guide-fr",
          title:
            "Le guide complet pour acheter un sac de luxe seconde main en Europe",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/fr/blog/authentifier-un-sac-de-luxe-guide-fr",
          title: "Comment authentifier un sac de luxe : le guide multimarques",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/fr/blog/entretien-sac-en-cuir-fr",
          title: "Entretenir un sac en cuir : la routine qui protège sa valeur",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/comprar-bolso-de-lujo-segunda-mano-guia-es",
          title:
            "La guía definitiva para comprar un bolso de lujo de segunda mano en Europa",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/es/blog/autenticar-un-bolso-de-lujo-guia-es",
          title:
            "Cómo autenticar un bolso de lujo: la guía para todas las marcas",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/es/blog/cuidar-bolsos-de-piel-es",
          title:
            "Cómo cuidar los bolsos de piel: la rutina que protege su valor",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/comprare-borsa-firmata-usata-guida-it",
          title:
            "La guida definitiva per comprare una borsa firmata usata in Europa",
          readingMinutes: 15,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/it/blog/autenticare-una-borsa-firmata-guida-it",
          title:
            "Come autenticare una borsa firmata: la guida per tutte le maison",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/it/blog/come-curare-borse-in-pelle-it",
          title:
            "Come curare le borse in pelle: la routine che ne protegge il valore",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
    },
  },
  {
    key: "/brands/chanel",
    urls: {
      en: "/brands/chanel",
      nl: "/merken/chanel",
      de: "/marken/chanel",
      fr: "/marques/chanel",
      es: "/marcas/chanel",
      it: "/marche/chanel",
    },
    links: {
      en: [
        {
          href: "/blog/secondhand-chanel-bags-buying-guide",
          title:
            "Buying a Secondhand Chanel Bag: My Honest Guide to Models, Sizes and Prices",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_chanel_bags_buying_guide_ai_3abc23385f.jpg",
            width: 500,
            height: 281,
            alt: "Quilted cream leather flap bag with gold chain on draped fabric",
          },
        },
        {
          href: "/blog/how-to-authenticate-a-chanel-bag",
          title:
            "How to Authenticate a Chanel Bag: 8 Checks You Can Run Yourself",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_chanel_bag_ai_c9cdf75f55.jpg",
            width: 500,
            height: 281,
            alt: "Black leather evening bag beside white inspection gloves",
          },
        },
        {
          href: "/blog/chanel-255-guide",
          title: "The Chanel 2.55: Why the February 1955 Original Still Wins",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_255_guide_ai_ee0b559a1b.jpg",
            width: 500,
            height: 281,
            alt: "Burgundy quilted shoulder bag with chain strap on a marble table",
          },
        },
        {
          href: "/blog/chanel-classic-flap-guide",
          title:
            "Chanel Classic Flap Guide: Sizes, Leathers, Hardware and the 2.55 Question",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_classic_flap_guide_ai_267580a053.jpg",
            width: 500,
            height: 281,
            alt: "Black quilted flap bag with gold chain on a velvet chair",
          },
        },
        {
          href: "/blog/chanel-bag-price-value-guide",
          title:
            "What Does a Chanel Bag Cost Secondhand? A Price and Value Guide",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_bag_price_value_guide_ai_ebbf1633ff.jpg",
            width: 500,
            height: 281,
            alt: "Quilted beige handbag on a desk with a notebook and pen",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/tweedehands-chanel-tas-kopen-gids-nl",
          title:
            "Een tweedehands Chanel-tas kopen: mijn eerlijke gids voor modellen, maten en prijzen",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_chanel_bags_buying_guide_ai_3abc23385f.jpg",
            width: 500,
            height: 281,
            alt: "Quilted cream leather flap bag with gold chain on draped fabric",
          },
        },
        {
          href: "/nl/blog/chanel-tas-echtheid-controleren-nl",
          title:
            "Chanel-tas checken op echtheid: 8 controles die je zelf kunt doen",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_chanel_bag_ai_c9cdf75f55.jpg",
            width: 500,
            height: 281,
            alt: "Black leather evening bag beside white inspection gloves",
          },
        },
        {
          href: "/nl/blog/chanel-2-55-gids-nl",
          title:
            "De Chanel 2.55: waarom het origineel uit februari 1955 nog altijd wint",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_255_guide_ai_ee0b559a1b.jpg",
            width: 500,
            height: 281,
            alt: "Burgundy quilted shoulder bag with chain strap on a marble table",
          },
        },
        {
          href: "/nl/blog/chanel-classic-flap-gids-nl",
          title:
            "Chanel Classic Flap gids: maten, leersoorten, hardware en de 2.55-vraag",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_classic_flap_guide_ai_267580a053.jpg",
            width: 500,
            height: 281,
            alt: "Black quilted flap bag with gold chain on a velvet chair",
          },
        },
        {
          href: "/nl/blog/chanel-tas-prijs-tweedehands-nl",
          title: "Wat kost een Chanel-tas tweedehands? Prijs- en waardegids",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_bag_price_value_guide_ai_ebbf1633ff.jpg",
            width: 500,
            height: 281,
            alt: "Quilted beige handbag on a desk with a notebook and pen",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/gebrauchte-chanel-tasche-kaufen-guide-de",
          title:
            "Eine gebrauchte Chanel-Tasche kaufen: mein ehrlicher Guide zu Modellen, Größen und Preisen",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_chanel_bags_buying_guide_ai_3abc23385f.jpg",
            width: 500,
            height: 281,
            alt: "Quilted cream leather flap bag with gold chain on draped fabric",
          },
        },
        {
          href: "/de/blog/chanel-tasche-echtheit-pruefen-de",
          title:
            "Chanel-Tasche auf Echtheit prüfen: 8 Checks, die Sie selbst machen können",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_chanel_bag_ai_c9cdf75f55.jpg",
            width: 500,
            height: 281,
            alt: "Black leather evening bag beside white inspection gloves",
          },
        },
        {
          href: "/de/blog/chanel-2-55-guide-de",
          title:
            "Die Chanel 2.55: warum das Original vom Februar 1955 immer noch gewinnt",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_255_guide_ai_ee0b559a1b.jpg",
            width: 500,
            height: 281,
            alt: "Burgundy quilted shoulder bag with chain strap on a marble table",
          },
        },
        {
          href: "/de/blog/chanel-classic-flap-guide-de",
          title:
            "Chanel Classic Flap Guide: Größen, Leder, Hardware und die 2.55-Frage",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_classic_flap_guide_ai_267580a053.jpg",
            width: 500,
            height: 281,
            alt: "Black quilted flap bag with gold chain on a velvet chair",
          },
        },
        {
          href: "/de/blog/chanel-tasche-preis-gebraucht-de",
          title:
            "Was kostet eine Chanel-Tasche gebraucht? Preis- und Wertguide",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_bag_price_value_guide_ai_ebbf1633ff.jpg",
            width: 500,
            height: 281,
            alt: "Quilted beige handbag on a desk with a notebook and pen",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/acheter-sac-chanel-seconde-main-guide-fr",
          title:
            "Acheter un sac Chanel seconde main : mon guide honnête des modèles, tailles et prix",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_chanel_bags_buying_guide_ai_3abc23385f.jpg",
            width: 500,
            height: 281,
            alt: "Quilted cream leather flap bag with gold chain on draped fabric",
          },
        },
        {
          href: "/fr/blog/authentifier-un-sac-chanel-fr",
          title:
            "Authentifier un sac Chanel : 8 vérifications à faire vous-même",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_chanel_bag_ai_c9cdf75f55.jpg",
            width: 500,
            height: 281,
            alt: "Black leather evening bag beside white inspection gloves",
          },
        },
        {
          href: "/fr/blog/chanel-2-55-guide-fr",
          title:
            "Le Chanel 2.55 : pourquoi l'original de février 1955 reste indépassable",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_255_guide_ai_ee0b559a1b.jpg",
            width: 500,
            height: 281,
            alt: "Burgundy quilted shoulder bag with chain strap on a marble table",
          },
        },
        {
          href: "/fr/blog/chanel-classic-flap-guide-fr",
          title:
            "Guide du Chanel Classic Flap : tailles, cuirs, métal et la question du 2.55",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_classic_flap_guide_ai_267580a053.jpg",
            width: 500,
            height: 281,
            alt: "Black quilted flap bag with gold chain on a velvet chair",
          },
        },
        {
          href: "/fr/blog/prix-sac-chanel-occasion-fr",
          title:
            "Combien coûte un sac Chanel d'occasion ? Guide des prix et de la valeur",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_bag_price_value_guide_ai_ebbf1633ff.jpg",
            width: 500,
            height: 281,
            alt: "Quilted beige handbag on a desk with a notebook and pen",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/comprar-bolso-chanel-segunda-mano-guia-es",
          title:
            "Comprar un bolso Chanel de segunda mano: mi guía honesta de modelos, tallas y precios",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_chanel_bags_buying_guide_ai_3abc23385f.jpg",
            width: 500,
            height: 281,
            alt: "Quilted cream leather flap bag with gold chain on draped fabric",
          },
        },
        {
          href: "/es/blog/autenticar-un-bolso-chanel-es",
          title:
            "Cómo autenticar un bolso Chanel: 8 comprobaciones que puedes hacer tú mismo",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_chanel_bag_ai_c9cdf75f55.jpg",
            width: 500,
            height: 281,
            alt: "Black leather evening bag beside white inspection gloves",
          },
        },
        {
          href: "/es/blog/chanel-2-55-guia-es",
          title:
            "El Chanel 2.55: por qué el original de febrero de 1955 sigue ganando",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_255_guide_ai_ee0b559a1b.jpg",
            width: 500,
            height: 281,
            alt: "Burgundy quilted shoulder bag with chain strap on a marble table",
          },
        },
        {
          href: "/es/blog/chanel-classic-flap-guia-es",
          title:
            "Guía del Chanel Classic Flap: tallas, pieles, herrajes y la cuestión del 2.55",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_classic_flap_guide_ai_267580a053.jpg",
            width: 500,
            height: 281,
            alt: "Black quilted flap bag with gold chain on a velvet chair",
          },
        },
        {
          href: "/es/blog/precio-bolso-chanel-segunda-mano-es",
          title:
            "¿Cuánto cuesta un bolso Chanel de segunda mano? Guía de precios y valor",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_bag_price_value_guide_ai_ebbf1633ff.jpg",
            width: 500,
            height: 281,
            alt: "Quilted beige handbag on a desk with a notebook and pen",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/comprare-borsa-chanel-usata-guida-it",
          title:
            "Comprare una borsa Chanel usata: la mia guida onesta a modelli, misure e prezzi",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_chanel_bags_buying_guide_ai_3abc23385f.jpg",
            width: 500,
            height: 281,
            alt: "Quilted cream leather flap bag with gold chain on draped fabric",
          },
        },
        {
          href: "/it/blog/autenticare-una-borsa-chanel-it",
          title:
            "Come autenticare una borsa Chanel: 8 controlli che puoi fare da solo",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_chanel_bag_ai_c9cdf75f55.jpg",
            width: 500,
            height: 281,
            alt: "Black leather evening bag beside white inspection gloves",
          },
        },
        {
          href: "/it/blog/chanel-2-55-guida-it",
          title:
            "La Chanel 2.55: perché l'originale del febbraio 1955 vince ancora",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_255_guide_ai_ee0b559a1b.jpg",
            width: 500,
            height: 281,
            alt: "Burgundy quilted shoulder bag with chain strap on a marble table",
          },
        },
        {
          href: "/it/blog/chanel-classic-flap-guida-it",
          title:
            "Guida alla Chanel Classic Flap: misure, pellami, hardware e la questione 2.55",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_classic_flap_guide_ai_267580a053.jpg",
            width: 500,
            height: 281,
            alt: "Black quilted flap bag with gold chain on a velvet chair",
          },
        },
        {
          href: "/it/blog/prezzo-borsa-chanel-usata-it",
          title: "Quanto costa una borsa Chanel usata? Guida a prezzi e valore",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_chanel_bag_price_value_guide_ai_ebbf1633ff.jpg",
            width: 500,
            height: 281,
            alt: "Quilted beige handbag on a desk with a notebook and pen",
          },
        },
      ],
    },
  },
  {
    key: "/brands/hermes",
    urls: {
      en: "/brands/hermes",
      nl: "/merken/hermes",
      de: "/marken/hermes",
      fr: "/marques/hermes",
      es: "/marcas/hermes",
      it: "/marche/hermes",
    },
    links: {
      en: [
        {
          href: "/blog/secondhand-hermes-bags-buying-guide",
          title: "How to Buy a Secondhand Hermes Bag: My Honest Guide",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_hermes_bags_buying_guide_ai_5f5a8a2ca4.jpg",
            width: 500,
            height: 281,
            alt: "Structured tan top-handle bag on a wooden shelf with dried grasses",
          },
        },
        {
          href: "/blog/hermes-birkin-guide",
          title:
            "Hermes Birkin Guide: Sizes, Leathers and Buying One Secondhand",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_birkin_guide_ai_6c4086b316.jpg",
            width: 500,
            height: 281,
            alt: "Structured cognac leather handbag on a stone windowsill",
          },
        },
        {
          href: "/blog/hermes-kelly-guide",
          title: "Hermes Kelly Guide: History, Sizes, Sellier vs Retourne",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_kelly_guide_ai_2ccb45932c.jpg",
            width: 500,
            height: 281,
            alt: "Trapezoid top-handle bag in deep green leather on a cafe table",
          },
        },
        {
          href: "/blog/how-to-authenticate-a-hermes-bag",
          title: "How to Authenticate a Hermes Bag: 8 Checks Before You Buy",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_hermes_bag_ai_396c00e81d.jpg",
            width: 500,
            height: 281,
            alt: "Hand-stitched edge of thick tan saddle leather",
          },
        },
        {
          href: "/blog/hermes-bag-price-value-guide",
          title: "What Hermes Bags Cost Secondhand: My Price and Value Guide",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_bag_price_value_guide_ai_876134a037.jpg",
            width: 500,
            height: 281,
            alt: "Tan structured handbag with a silk scarf on linen",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/tweedehands-hermes-tas-kopen-gids-nl",
          title: "Een tweedehands Hermes-tas kopen: mijn eerlijke gids",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_hermes_bags_buying_guide_ai_5f5a8a2ca4.jpg",
            width: 500,
            height: 281,
            alt: "Structured tan top-handle bag on a wooden shelf with dried grasses",
          },
        },
        {
          href: "/nl/blog/hermes-birkin-gids-nl",
          title: "Hermes Birkin gids: maten, leersoorten en tweedehands kopen",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_birkin_guide_ai_6c4086b316.jpg",
            width: 500,
            height: 281,
            alt: "Structured cognac leather handbag on a stone windowsill",
          },
        },
        {
          href: "/nl/blog/hermes-kelly-gids-nl",
          title:
            "Hermes Kelly gids: geschiedenis, maten, sellier versus retourné",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_kelly_guide_ai_2ccb45932c.jpg",
            width: 500,
            height: 281,
            alt: "Trapezoid top-handle bag in deep green leather on a cafe table",
          },
        },
        {
          href: "/nl/blog/hermes-tas-echtheid-controleren-nl",
          title: "Hermes-tas checken op echtheid: 8 controles voordat je koopt",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_hermes_bag_ai_396c00e81d.jpg",
            width: 500,
            height: 281,
            alt: "Hand-stitched edge of thick tan saddle leather",
          },
        },
        {
          href: "/nl/blog/hermes-tas-prijs-tweedehands-nl",
          title:
            "Wat kost een Hermes-tas tweedehands? Mijn prijs- en waardegids",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_bag_price_value_guide_ai_876134a037.jpg",
            width: 500,
            height: 281,
            alt: "Tan structured handbag with a silk scarf on linen",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/gebrauchte-hermes-tasche-kaufen-guide-de",
          title: "Eine gebrauchte Hermes-Tasche kaufen: mein ehrlicher Guide",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_hermes_bags_buying_guide_ai_5f5a8a2ca4.jpg",
            width: 500,
            height: 281,
            alt: "Structured tan top-handle bag on a wooden shelf with dried grasses",
          },
        },
        {
          href: "/de/blog/hermes-birkin-guide-de",
          title: "Hermes Birkin Guide: Größen, Leder und der Gebrauchtkauf",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_birkin_guide_ai_6c4086b316.jpg",
            width: 500,
            height: 281,
            alt: "Structured cognac leather handbag on a stone windowsill",
          },
        },
        {
          href: "/de/blog/hermes-kelly-guide-de",
          title:
            "Hermes Kelly Guide: Geschichte, Größen, Sellier versus Retourné",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_kelly_guide_ai_2ccb45932c.jpg",
            width: 500,
            height: 281,
            alt: "Trapezoid top-handle bag in deep green leather on a cafe table",
          },
        },
        {
          href: "/de/blog/hermes-tasche-echtheit-pruefen-de",
          title: "Hermes-Tasche auf Echtheit prüfen: 8 Checks vor dem Kauf",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_hermes_bag_ai_396c00e81d.jpg",
            width: 500,
            height: 281,
            alt: "Hand-stitched edge of thick tan saddle leather",
          },
        },
        {
          href: "/de/blog/hermes-tasche-preis-gebraucht-de",
          title:
            "Was kosten Hermes-Taschen gebraucht? Mein Preis- und Wertguide",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_bag_price_value_guide_ai_876134a037.jpg",
            width: 500,
            height: 281,
            alt: "Tan structured handbag with a silk scarf on linen",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/acheter-sac-hermes-seconde-main-guide-fr",
          title: "Acheter un sac Hermes seconde main : mon guide honnête",
          readingMinutes: 10,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_hermes_bags_buying_guide_ai_5f5a8a2ca4.jpg",
            width: 500,
            height: 281,
            alt: "Structured tan top-handle bag on a wooden shelf with dried grasses",
          },
        },
        {
          href: "/fr/blog/hermes-birkin-guide-fr",
          title:
            "Guide du Hermes Birkin : tailles, cuirs et achat en seconde main",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_birkin_guide_ai_6c4086b316.jpg",
            width: 500,
            height: 281,
            alt: "Structured cognac leather handbag on a stone windowsill",
          },
        },
        {
          href: "/fr/blog/hermes-kelly-guide-fr",
          title:
            "Guide du Hermes Kelly : histoire, tailles, sellier ou retourné",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_kelly_guide_ai_2ccb45932c.jpg",
            width: 500,
            height: 281,
            alt: "Trapezoid top-handle bag in deep green leather on a cafe table",
          },
        },
        {
          href: "/fr/blog/authentifier-un-sac-hermes-fr",
          title: "Authentifier un sac Hermes : 8 vérifications avant d'acheter",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_hermes_bag_ai_396c00e81d.jpg",
            width: 500,
            height: 281,
            alt: "Hand-stitched edge of thick tan saddle leather",
          },
        },
        {
          href: "/fr/blog/prix-sac-hermes-occasion-fr",
          title:
            "Combien coûtent les sacs Hermes d'occasion ? Mon guide des prix et de la valeur",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_bag_price_value_guide_ai_876134a037.jpg",
            width: 500,
            height: 281,
            alt: "Tan structured handbag with a silk scarf on linen",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/comprar-bolso-hermes-segunda-mano-guia-es",
          title:
            "Cómo comprar un bolso Hermes de segunda mano: mi guía honesta",
          readingMinutes: 10,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_hermes_bags_buying_guide_ai_5f5a8a2ca4.jpg",
            width: 500,
            height: 281,
            alt: "Structured tan top-handle bag on a wooden shelf with dried grasses",
          },
        },
        {
          href: "/es/blog/hermes-birkin-guia-es",
          title:
            "Guía del Hermes Birkin: tallas, pieles y cómo comprarlo de segunda mano",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_birkin_guide_ai_6c4086b316.jpg",
            width: 500,
            height: 281,
            alt: "Structured cognac leather handbag on a stone windowsill",
          },
        },
        {
          href: "/es/blog/hermes-kelly-guia-es",
          title:
            "Guía del Hermes Kelly: historia, tallas, sellier frente a retourné",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_kelly_guide_ai_2ccb45932c.jpg",
            width: 500,
            height: 281,
            alt: "Trapezoid top-handle bag in deep green leather on a cafe table",
          },
        },
        {
          href: "/es/blog/autenticar-un-bolso-hermes-es",
          title:
            "Cómo autenticar un bolso Hermes: 8 comprobaciones antes de comprar",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_hermes_bag_ai_396c00e81d.jpg",
            width: 500,
            height: 281,
            alt: "Hand-stitched edge of thick tan saddle leather",
          },
        },
        {
          href: "/es/blog/precio-bolso-hermes-segunda-mano-es",
          title:
            "¿Cuánto cuestan los bolsos Hermes de segunda mano? Mi guía de precios y valor",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_bag_price_value_guide_ai_876134a037.jpg",
            width: 500,
            height: 281,
            alt: "Tan structured handbag with a silk scarf on linen",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/comprare-borsa-hermes-usata-guida-it",
          title: "Come comprare una borsa Hermes usata: la mia guida onesta",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_hermes_bags_buying_guide_ai_5f5a8a2ca4.jpg",
            width: 500,
            height: 281,
            alt: "Structured tan top-handle bag on a wooden shelf with dried grasses",
          },
        },
        {
          href: "/it/blog/hermes-birkin-guida-it",
          title:
            "Guida alla Hermes Birkin: misure, pellami e come comprarla usata",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_birkin_guide_ai_6c4086b316.jpg",
            width: 500,
            height: 281,
            alt: "Structured cognac leather handbag on a stone windowsill",
          },
        },
        {
          href: "/it/blog/hermes-kelly-guida-it",
          title: "Guida alla Hermes Kelly: storia, misure, sellier o retourné",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_kelly_guide_ai_2ccb45932c.jpg",
            width: 500,
            height: 281,
            alt: "Trapezoid top-handle bag in deep green leather on a cafe table",
          },
        },
        {
          href: "/it/blog/autenticare-una-borsa-hermes-it",
          title:
            "Come autenticare una borsa Hermes: 8 controlli prima di comprare",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_hermes_bag_ai_396c00e81d.jpg",
            width: 500,
            height: 281,
            alt: "Hand-stitched edge of thick tan saddle leather",
          },
        },
        {
          href: "/it/blog/prezzo-borsa-hermes-usata-it",
          title:
            "Quanto costano le borse Hermes usate? La mia guida a prezzi e valore",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_hermes_bag_price_value_guide_ai_876134a037.jpg",
            width: 500,
            height: 281,
            alt: "Tan structured handbag with a silk scarf on linen",
          },
        },
      ],
    },
  },
  {
    key: "/brands/louis-vuitton",
    urls: {
      en: "/brands/louis-vuitton",
      nl: "/merken/louis-vuitton",
      de: "/marken/louis-vuitton",
      fr: "/marques/louis-vuitton",
      es: "/marcas/louis-vuitton",
      it: "/marche/louis-vuitton",
    },
    links: {
      en: [
        {
          href: "/blog/secondhand-louis-vuitton-bags-buying-guide",
          title:
            "Buying a Secondhand Louis Vuitton Bag: My Honest Guide to Models and Canvas",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
        {
          href: "/blog/louis-vuitton-speedy-guide",
          title:
            "Louis Vuitton Speedy Guide: Sizes 25 to 40, History and Buying Secondhand",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/blog/louis-vuitton-neverfull-guide",
          title:
            "Louis Vuitton Neverfull Guide: PM, MM, GM and Why It Sells So Fast",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/blog/how-to-authenticate-a-louis-vuitton-bag",
          title:
            "How to Authenticate a Louis Vuitton Bag: Date Codes Plus 8 Checks",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_louis_vuitton_bag_ai_a80174b989.jpg",
            width: 500,
            height: 281,
            alt: "Close view of stitching and a brass zip pull on a tan leather handbag",
          },
        },
        {
          href: "/blog/louis-vuitton-bag-price-value-guide",
          title:
            "What Does a Louis Vuitton Bag Cost Secondhand? A Price and Value Guide",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_bag_price_value_guide_ai_e83e92656c.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a wooden table in warm daylight",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/tweedehands-louis-vuitton-tas-kopen-gids-nl",
          title:
            "Een tweedehands Louis Vuitton-tas kopen: mijn eerlijke gids voor modellen en canvas",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
        {
          href: "/nl/blog/louis-vuitton-speedy-gids-nl",
          title:
            "Louis Vuitton Speedy gids: maten 25 tot 40, geschiedenis en tweedehands kopen",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/nl/blog/louis-vuitton-neverfull-gids-nl",
          title:
            "Louis Vuitton Neverfull gids: PM, MM, GM en waarom hij zo snel verkocht is",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/nl/blog/louis-vuitton-tas-echtheid-controleren-nl",
          title:
            "Louis Vuitton-tas checken op echtheid: datumcodes plus 8 controles",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_louis_vuitton_bag_ai_a80174b989.jpg",
            width: 500,
            height: 281,
            alt: "Close view of stitching and a brass zip pull on a tan leather handbag",
          },
        },
        {
          href: "/nl/blog/louis-vuitton-tas-prijs-tweedehands-nl",
          title:
            "Wat kost een Louis Vuitton-tas tweedehands? Prijs- en waardegids",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_bag_price_value_guide_ai_e83e92656c.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a wooden table in warm daylight",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/gebrauchte-louis-vuitton-tasche-kaufen-guide-de",
          title:
            "Eine gebrauchte Louis Vuitton Tasche kaufen: mein ehrlicher Guide zu Modellen und Canvas",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
        {
          href: "/de/blog/louis-vuitton-speedy-guide-de",
          title:
            "Louis Vuitton Speedy Guide: Größen 25 bis 40, Geschichte und Gebrauchtkauf",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/de/blog/louis-vuitton-neverfull-guide-de",
          title:
            "Louis Vuitton Neverfull Guide: PM, MM, GM und warum sie so schnell weg ist",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/de/blog/louis-vuitton-tasche-echtheit-pruefen-de",
          title:
            "Louis Vuitton Tasche auf Echtheit prüfen: Datumscodes plus 8 Checks",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_louis_vuitton_bag_ai_a80174b989.jpg",
            width: 500,
            height: 281,
            alt: "Close view of stitching and a brass zip pull on a tan leather handbag",
          },
        },
        {
          href: "/de/blog/louis-vuitton-tasche-preis-gebraucht-de",
          title:
            "Was kostet eine Louis Vuitton Tasche gebraucht? Preis- und Wertguide",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_bag_price_value_guide_ai_e83e92656c.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a wooden table in warm daylight",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/acheter-sac-louis-vuitton-seconde-main-guide-fr",
          title:
            "Acheter un sac Louis Vuitton seconde main : mon guide honnête des modèles et des toiles",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
        {
          href: "/fr/blog/louis-vuitton-speedy-guide-fr",
          title:
            "Guide du Louis Vuitton Speedy : tailles 25 à 40, histoire et achat en seconde main",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/fr/blog/louis-vuitton-neverfull-guide-fr",
          title:
            "Guide du Louis Vuitton Neverfull : PM, MM, GM et pourquoi il part si vite",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/fr/blog/authentifier-un-sac-louis-vuitton-fr",
          title:
            "Authentifier un sac Louis Vuitton : les codes date et 8 vérifications",
          readingMinutes: 10,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_louis_vuitton_bag_ai_a80174b989.jpg",
            width: 500,
            height: 281,
            alt: "Close view of stitching and a brass zip pull on a tan leather handbag",
          },
        },
        {
          href: "/fr/blog/prix-sac-louis-vuitton-occasion-fr",
          title:
            "Combien coûte un sac Louis Vuitton d'occasion ? Guide des prix et de la valeur",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_bag_price_value_guide_ai_e83e92656c.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a wooden table in warm daylight",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/comprar-bolso-louis-vuitton-segunda-mano-guia-es",
          title:
            "Comprar un bolso Louis Vuitton de segunda mano: mi guía honesta de modelos y lonas",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
        {
          href: "/es/blog/louis-vuitton-speedy-guia-es",
          title:
            "Guía del Louis Vuitton Speedy: tallas de la 25 a la 40, historia y compra de segunda mano",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/es/blog/louis-vuitton-neverfull-guia-es",
          title:
            "Guía del Louis Vuitton Neverfull: PM, MM, GM y por qué se vende tan rápido",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/es/blog/autenticar-un-bolso-louis-vuitton-es",
          title:
            "Cómo autenticar un bolso Louis Vuitton: códigos de fecha y 8 comprobaciones",
          readingMinutes: 10,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_louis_vuitton_bag_ai_a80174b989.jpg",
            width: 500,
            height: 281,
            alt: "Close view of stitching and a brass zip pull on a tan leather handbag",
          },
        },
        {
          href: "/es/blog/precio-bolso-louis-vuitton-segunda-mano-es",
          title:
            "¿Cuánto cuesta un bolso Louis Vuitton de segunda mano? Guía de precios y valor",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_bag_price_value_guide_ai_e83e92656c.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a wooden table in warm daylight",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/comprare-borsa-louis-vuitton-usata-guida-it",
          title:
            "Comprare una borsa Louis Vuitton usata: la mia guida onesta a modelli e tele",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_louis_vuitton_bags_buying_guide_ai_a3a00184d0.jpg",
            width: 500,
            height: 281,
            alt: "Three caramel canvas handbags with leather trim on a vintage trunk",
          },
        },
        {
          href: "/it/blog/louis-vuitton-speedy-guida-it",
          title:
            "Guida alla Louis Vuitton Speedy: misure dalla 25 alla 40, storia e acquisto usato",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_speedy_guide_ai_1e44442adb.jpg",
            width: 500,
            height: 281,
            alt: "Rounded barrel-shaped tan leather handbag with two rolled handles",
          },
        },
        {
          href: "/it/blog/louis-vuitton-neverfull-guida-it",
          title:
            "Guida alla Louis Vuitton Neverfull: PM, MM, GM e perché si vende così in fretta",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_neverfull_guide_ai_d0d33f2ab6.jpg",
            width: 500,
            height: 281,
            alt: "Open beige canvas tote with long flat leather handles and side lacing",
          },
        },
        {
          href: "/it/blog/autenticare-una-borsa-louis-vuitton-it",
          title:
            "Come autenticare una borsa Louis Vuitton: codici data e 8 controlli",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_louis_vuitton_bag_ai_a80174b989.jpg",
            width: 500,
            height: 281,
            alt: "Close view of stitching and a brass zip pull on a tan leather handbag",
          },
        },
        {
          href: "/it/blog/prezzo-borsa-louis-vuitton-usata-it",
          title:
            "Quanto costa una borsa Louis Vuitton usata? Guida a prezzi e valore",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_louis_vuitton_bag_price_value_guide_ai_e83e92656c.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a wooden table in warm daylight",
          },
        },
      ],
    },
  },
  {
    key: "/brands/dior",
    urls: {
      en: "/brands/dior",
      nl: "/merken/dior",
      de: "/marken/dior",
      fr: "/marques/dior",
      es: "/marcas/dior",
      it: "/marche/dior",
    },
    links: {
      en: [
        {
          href: "/blog/secondhand-dior-bags-buying-guide",
          title:
            "Buying a Secondhand Dior Bag: My Honest Guide to Lady Dior, Saddle and More",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_dior_bags_buying_guide_ai_3fd71146bc.jpg",
            width: 500,
            height: 281,
            alt: "Cream quilted top-handle bag and black saddle-shaped bag on marble",
          },
        },
        {
          href: "/blog/dior-lady-dior-guide",
          title:
            "Lady Dior Guide: The Bag Named for Princess Diana, Sizes and Secondhand Buying",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_lady_dior_guide_ai_ea94d74772.jpg",
            width: 500,
            height: 281,
            alt: "Structured cream quilted top-handle bag with gold-tone charms",
          },
        },
        {
          href: "/blog/dior-saddle-guide",
          title:
            "Dior Saddle Guide: Galliano's 2000 Original vs the 2018 Revival",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_saddle_guide_ai_941a465e13.jpg",
            width: 500,
            height: 281,
            alt: "Curved black leather saddle-shaped shoulder bag on a saddle blanket",
          },
        },
        {
          href: "/blog/how-to-authenticate-a-dior-bag",
          title:
            "How to Authenticate a Dior Bag: 8 Checks You Can Run Yourself",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_dior_bag_ai_dd603a0b65.jpg",
            width: 500,
            height: 281,
            alt: "Close view of square quilting and a gold-tone clasp on a cream handbag",
          },
        },
        {
          href: "/blog/dior-bag-price-value-guide",
          title:
            "What Does a Dior Bag Cost Secondhand? A Price and Value Guide",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_bag_price_value_guide_ai_f2d182e093.jpg",
            width: 500,
            height: 281,
            alt: "Cream quilted handbag on a stone plinth in a minimal gallery",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/tweedehands-dior-tas-kopen-gids-nl",
          title:
            "Een tweedehands Dior-tas kopen: mijn eerlijke gids voor Lady Dior, Saddle en meer",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_dior_bags_buying_guide_ai_3fd71146bc.jpg",
            width: 500,
            height: 281,
            alt: "Cream quilted top-handle bag and black saddle-shaped bag on marble",
          },
        },
        {
          href: "/nl/blog/dior-lady-dior-gids-nl",
          title:
            "Lady Dior gids: de tas vernoemd naar prinses Diana, maten en tweedehands kopen",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_lady_dior_guide_ai_ea94d74772.jpg",
            width: 500,
            height: 281,
            alt: "Structured cream quilted top-handle bag with gold-tone charms",
          },
        },
        {
          href: "/nl/blog/dior-saddle-gids-nl",
          title:
            "Dior Saddle gids: het origineel van Galliano uit 2000 versus de revival van 2018",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_saddle_guide_ai_941a465e13.jpg",
            width: 500,
            height: 281,
            alt: "Curved black leather saddle-shaped shoulder bag on a saddle blanket",
          },
        },
        {
          href: "/nl/blog/dior-tas-echtheid-controleren-nl",
          title:
            "Dior-tas checken op echtheid: 8 controles die je zelf kunt doen",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_dior_bag_ai_dd603a0b65.jpg",
            width: 500,
            height: 281,
            alt: "Close view of square quilting and a gold-tone clasp on a cream handbag",
          },
        },
        {
          href: "/nl/blog/dior-tas-prijs-tweedehands-nl",
          title: "Wat kost een Dior-tas tweedehands? Prijs- en waardegids",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_bag_price_value_guide_ai_f2d182e093.jpg",
            width: 500,
            height: 281,
            alt: "Cream quilted handbag on a stone plinth in a minimal gallery",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/gebrauchte-dior-tasche-kaufen-guide-de",
          title:
            "Eine gebrauchte Dior Tasche kaufen: mein ehrlicher Guide zu Lady Dior, Saddle und mehr",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_dior_bags_buying_guide_ai_3fd71146bc.jpg",
            width: 500,
            height: 281,
            alt: "Cream quilted top-handle bag and black saddle-shaped bag on marble",
          },
        },
        {
          href: "/de/blog/dior-lady-dior-guide-de",
          title:
            "Lady Dior Guide: die Tasche, die Prinzessin Diana ihren Namen gab, Größen und Gebrauchtkauf",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_lady_dior_guide_ai_ea94d74772.jpg",
            width: 500,
            height: 281,
            alt: "Structured cream quilted top-handle bag with gold-tone charms",
          },
        },
        {
          href: "/de/blog/dior-saddle-guide-de",
          title:
            "Dior Saddle Guide: Gallianos Original von 2000 gegen das Revival von 2018",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_saddle_guide_ai_941a465e13.jpg",
            width: 500,
            height: 281,
            alt: "Curved black leather saddle-shaped shoulder bag on a saddle blanket",
          },
        },
        {
          href: "/de/blog/dior-tasche-echtheit-pruefen-de",
          title:
            "Dior Tasche auf Echtheit prüfen: 8 Checks, die Sie selbst machen können",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_dior_bag_ai_dd603a0b65.jpg",
            width: 500,
            height: 281,
            alt: "Close view of square quilting and a gold-tone clasp on a cream handbag",
          },
        },
        {
          href: "/de/blog/dior-tasche-preis-gebraucht-de",
          title: "Was kostet eine Dior Tasche gebraucht? Preis- und Wertguide",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_bag_price_value_guide_ai_f2d182e093.jpg",
            width: 500,
            height: 281,
            alt: "Cream quilted handbag on a stone plinth in a minimal gallery",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/acheter-sac-dior-seconde-main-guide-fr",
          title:
            "Acheter un sac Dior seconde main : mon guide honnête du Lady Dior, du Saddle et du reste",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_dior_bags_buying_guide_ai_3fd71146bc.jpg",
            width: 500,
            height: 281,
            alt: "Cream quilted top-handle bag and black saddle-shaped bag on marble",
          },
        },
        {
          href: "/fr/blog/dior-lady-dior-guide-fr",
          title:
            "Guide du Lady Dior : le sac qui doit son nom à la princesse Diana, tailles et seconde main",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_lady_dior_guide_ai_ea94d74772.jpg",
            width: 500,
            height: 281,
            alt: "Structured cream quilted top-handle bag with gold-tone charms",
          },
        },
        {
          href: "/fr/blog/dior-saddle-guide-fr",
          title:
            "Guide du Dior Saddle : l'original de Galliano en 2000 face au retour de 2018",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_saddle_guide_ai_941a465e13.jpg",
            width: 500,
            height: 281,
            alt: "Curved black leather saddle-shaped shoulder bag on a saddle blanket",
          },
        },
        {
          href: "/fr/blog/authentifier-un-sac-dior-fr",
          title: "Authentifier un sac Dior : 8 vérifications à faire vous-même",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_dior_bag_ai_dd603a0b65.jpg",
            width: 500,
            height: 281,
            alt: "Close view of square quilting and a gold-tone clasp on a cream handbag",
          },
        },
        {
          href: "/fr/blog/prix-sac-dior-occasion-fr",
          title:
            "Combien coûte un sac Dior d'occasion ? Guide des prix et de la valeur",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_bag_price_value_guide_ai_f2d182e093.jpg",
            width: 500,
            height: 281,
            alt: "Cream quilted handbag on a stone plinth in a minimal gallery",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/comprar-bolso-dior-segunda-mano-guia-es",
          title:
            "Comprar un bolso Dior de segunda mano: mi guía honesta del Lady Dior, el Saddle y más",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_dior_bags_buying_guide_ai_3fd71146bc.jpg",
            width: 500,
            height: 281,
            alt: "Cream quilted top-handle bag and black saddle-shaped bag on marble",
          },
        },
        {
          href: "/es/blog/dior-lady-dior-guia-es",
          title:
            "Guía del Lady Dior: el bolso que debe su nombre a la princesa Diana, tallas y segunda mano",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_lady_dior_guide_ai_ea94d74772.jpg",
            width: 500,
            height: 281,
            alt: "Structured cream quilted top-handle bag with gold-tone charms",
          },
        },
        {
          href: "/es/blog/dior-saddle-guia-es",
          title:
            "Guía del Dior Saddle: el original de Galliano de 2000 frente al regreso de 2018",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_saddle_guide_ai_941a465e13.jpg",
            width: 500,
            height: 281,
            alt: "Curved black leather saddle-shaped shoulder bag on a saddle blanket",
          },
        },
        {
          href: "/es/blog/autenticar-un-bolso-dior-es",
          title:
            "Cómo autenticar un bolso Dior: 8 comprobaciones que puedes hacer tú mismo",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_dior_bag_ai_dd603a0b65.jpg",
            width: 500,
            height: 281,
            alt: "Close view of square quilting and a gold-tone clasp on a cream handbag",
          },
        },
        {
          href: "/es/blog/precio-bolso-dior-segunda-mano-es",
          title:
            "¿Cuánto cuesta un bolso Dior de segunda mano? Guía de precios y valor",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_bag_price_value_guide_ai_f2d182e093.jpg",
            width: 500,
            height: 281,
            alt: "Cream quilted handbag on a stone plinth in a minimal gallery",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/comprare-borsa-dior-usata-guida-it",
          title:
            "Comprare una borsa Dior usata: la mia guida onesta a Lady Dior, Saddle e non solo",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_dior_bags_buying_guide_ai_3fd71146bc.jpg",
            width: 500,
            height: 281,
            alt: "Cream quilted top-handle bag and black saddle-shaped bag on marble",
          },
        },
        {
          href: "/it/blog/dior-lady-dior-guida-it",
          title:
            "Guida alla Lady Dior: la borsa che deve il nome alla principessa Diana, misure e usato",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_lady_dior_guide_ai_ea94d74772.jpg",
            width: 500,
            height: 281,
            alt: "Structured cream quilted top-handle bag with gold-tone charms",
          },
        },
        {
          href: "/it/blog/dior-saddle-guida-it",
          title:
            "Guida alla Dior Saddle: l'originale di Galliano del 2000 contro il ritorno del 2018",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_saddle_guide_ai_941a465e13.jpg",
            width: 500,
            height: 281,
            alt: "Curved black leather saddle-shaped shoulder bag on a saddle blanket",
          },
        },
        {
          href: "/it/blog/autenticare-una-borsa-dior-it",
          title:
            "Come autenticare una borsa Dior: 8 controlli che puoi fare da solo",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_dior_bag_ai_dd603a0b65.jpg",
            width: 500,
            height: 281,
            alt: "Close view of square quilting and a gold-tone clasp on a cream handbag",
          },
        },
        {
          href: "/it/blog/prezzo-borsa-dior-usata-it",
          title: "Quanto costa una borsa Dior usata? Guida a prezzi e valore",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_dior_bag_price_value_guide_ai_f2d182e093.jpg",
            width: 500,
            height: 281,
            alt: "Cream quilted handbag on a stone plinth in a minimal gallery",
          },
        },
      ],
    },
  },
  {
    key: "/brands/gucci",
    urls: {
      en: "/brands/gucci",
      nl: "/merken/gucci",
      de: "/marken/gucci",
      fr: "/marques/gucci",
      es: "/marcas/gucci",
      it: "/marche/gucci",
    },
    links: {
      en: [
        {
          href: "/blog/secondhand-gucci-bags-buying-guide",
          title:
            "Buying a Secondhand Gucci Bag: My Honest Guide to Canvas, Icons and Prices",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_gucci_bags_buying_guide_ai_81efe96a4e.jpg",
            width: 500,
            height: 281,
            alt: "Tan, burgundy and green leather handbags on a walnut sideboard",
          },
        },
        {
          href: "/blog/gucci-marmont-guide",
          title:
            "Gucci GG Marmont Guide: Sizes, the 2016 Design and Buying One Secondhand",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_marmont_guide_ai_8d6efa0d90.jpg",
            width: 500,
            height: 281,
            alt: "Chevron-quilted dusty pink shoulder bag with a chain strap",
          },
        },
        {
          href: "/blog/gucci-jackie-guide",
          title:
            "Gucci Jackie Guide: The 1961 Hobo, the 2020 Revival and Secondhand Buying",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_jackie_guide_ai_ee5b8b6043.jpg",
            width: 500,
            height: 281,
            alt: "Crescent-shaped tan leather hobo bag with a piston clasp",
          },
        },
        {
          href: "/blog/how-to-authenticate-a-gucci-bag",
          title:
            "How to Authenticate a Gucci Bag: 8 Checks You Can Run Yourself",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_gucci_bag_ai_2a67af1e93.jpg",
            width: 500,
            height: 281,
            alt: "Dark green leather shoulder bag with gold-tone hardware on linen",
          },
        },
        {
          href: "/blog/gucci-bag-price-value-guide",
          title:
            "What Does a Gucci Bag Cost Secondhand? A Price and Value Guide",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_bag_price_value_guide_ai_826a486dd5.jpg",
            width: 500,
            height: 281,
            alt: "Burgundy leather shoulder bag with gold-tone hardware on walnut",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/tweedehands-gucci-tas-kopen-gids-nl",
          title:
            "Een tweedehands Gucci-tas kopen: mijn eerlijke gids voor canvas, iconen en prijzen",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_gucci_bags_buying_guide_ai_81efe96a4e.jpg",
            width: 500,
            height: 281,
            alt: "Tan, burgundy and green leather handbags on a walnut sideboard",
          },
        },
        {
          href: "/nl/blog/gucci-gg-marmont-gids-nl",
          title:
            "Gucci GG Marmont gids: maten, het ontwerp uit 2016 en tweedehands kopen",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_marmont_guide_ai_8d6efa0d90.jpg",
            width: 500,
            height: 281,
            alt: "Chevron-quilted dusty pink shoulder bag with a chain strap",
          },
        },
        {
          href: "/nl/blog/gucci-jackie-gids-nl",
          title:
            "Gucci Jackie gids: de hobo uit 1961, de revival van 2020 en tweedehands kopen",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_jackie_guide_ai_ee5b8b6043.jpg",
            width: 500,
            height: 281,
            alt: "Crescent-shaped tan leather hobo bag with a piston clasp",
          },
        },
        {
          href: "/nl/blog/gucci-tas-echtheid-controleren-nl",
          title:
            "Gucci-tas checken op echtheid: 8 controles die je zelf kunt doen",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_gucci_bag_ai_2a67af1e93.jpg",
            width: 500,
            height: 281,
            alt: "Dark green leather shoulder bag with gold-tone hardware on linen",
          },
        },
        {
          href: "/nl/blog/gucci-tas-prijs-tweedehands-nl",
          title: "Wat kost een Gucci-tas tweedehands? Prijs- en waardegids",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_bag_price_value_guide_ai_826a486dd5.jpg",
            width: 500,
            height: 281,
            alt: "Burgundy leather shoulder bag with gold-tone hardware on walnut",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/gebrauchte-gucci-tasche-kaufen-guide-de",
          title:
            "Eine gebrauchte Gucci Tasche kaufen: mein ehrlicher Guide zu Canvas, Ikonen und Preisen",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_gucci_bags_buying_guide_ai_81efe96a4e.jpg",
            width: 500,
            height: 281,
            alt: "Tan, burgundy and green leather handbags on a walnut sideboard",
          },
        },
        {
          href: "/de/blog/gucci-gg-marmont-guide-de",
          title:
            "Gucci GG Marmont Guide: Größen, das Design von 2016 und der Gebrauchtkauf",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_marmont_guide_ai_8d6efa0d90.jpg",
            width: 500,
            height: 281,
            alt: "Chevron-quilted dusty pink shoulder bag with a chain strap",
          },
        },
        {
          href: "/de/blog/gucci-jackie-guide-de",
          title:
            "Gucci Jackie Guide: die Hobo von 1961, das Revival von 2020 und der Gebrauchtkauf",
          readingMinutes: 6,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_jackie_guide_ai_ee5b8b6043.jpg",
            width: 500,
            height: 281,
            alt: "Crescent-shaped tan leather hobo bag with a piston clasp",
          },
        },
        {
          href: "/de/blog/gucci-tasche-echtheit-pruefen-de",
          title:
            "Gucci Tasche auf Echtheit prüfen: 8 Checks, die Sie selbst machen können",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_gucci_bag_ai_2a67af1e93.jpg",
            width: 500,
            height: 281,
            alt: "Dark green leather shoulder bag with gold-tone hardware on linen",
          },
        },
        {
          href: "/de/blog/gucci-tasche-preis-gebraucht-de",
          title: "Was kostet eine Gucci Tasche gebraucht? Preis- und Wertguide",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_bag_price_value_guide_ai_826a486dd5.jpg",
            width: 500,
            height: 281,
            alt: "Burgundy leather shoulder bag with gold-tone hardware on walnut",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/acheter-sac-gucci-seconde-main-guide-fr",
          title:
            "Acheter un sac Gucci seconde main : mon guide honnête des toiles, des icônes et des prix",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_gucci_bags_buying_guide_ai_81efe96a4e.jpg",
            width: 500,
            height: 281,
            alt: "Tan, burgundy and green leather handbags on a walnut sideboard",
          },
        },
        {
          href: "/fr/blog/gucci-gg-marmont-guide-fr",
          title:
            "Guide du Gucci GG Marmont : tailles, le dessin de 2016 et l'achat en seconde main",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_marmont_guide_ai_8d6efa0d90.jpg",
            width: 500,
            height: 281,
            alt: "Chevron-quilted dusty pink shoulder bag with a chain strap",
          },
        },
        {
          href: "/fr/blog/gucci-jackie-guide-fr",
          title:
            "Guide du Gucci Jackie : le hobo de 1961, le retour de 2020 et la seconde main",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_jackie_guide_ai_ee5b8b6043.jpg",
            width: 500,
            height: 281,
            alt: "Crescent-shaped tan leather hobo bag with a piston clasp",
          },
        },
        {
          href: "/fr/blog/authentifier-un-sac-gucci-fr",
          title:
            "Authentifier un sac Gucci : 8 vérifications à faire vous-même",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_gucci_bag_ai_2a67af1e93.jpg",
            width: 500,
            height: 281,
            alt: "Dark green leather shoulder bag with gold-tone hardware on linen",
          },
        },
        {
          href: "/fr/blog/prix-sac-gucci-occasion-fr",
          title:
            "Combien coûte un sac Gucci d'occasion ? Guide des prix et de la valeur",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_bag_price_value_guide_ai_826a486dd5.jpg",
            width: 500,
            height: 281,
            alt: "Burgundy leather shoulder bag with gold-tone hardware on walnut",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/comprar-bolso-gucci-segunda-mano-guia-es",
          title:
            "Comprar un bolso Gucci de segunda mano: mi guía honesta de lonas, iconos y precios",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_gucci_bags_buying_guide_ai_81efe96a4e.jpg",
            width: 500,
            height: 281,
            alt: "Tan, burgundy and green leather handbags on a walnut sideboard",
          },
        },
        {
          href: "/es/blog/gucci-gg-marmont-guia-es",
          title:
            "Guía del Gucci GG Marmont: tallas, el diseño de 2016 y la compra de segunda mano",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_marmont_guide_ai_8d6efa0d90.jpg",
            width: 500,
            height: 281,
            alt: "Chevron-quilted dusty pink shoulder bag with a chain strap",
          },
        },
        {
          href: "/es/blog/gucci-jackie-guia-es",
          title:
            "Guía del Gucci Jackie: el hobo de 1961, el regreso de 2020 y la segunda mano",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_jackie_guide_ai_ee5b8b6043.jpg",
            width: 500,
            height: 281,
            alt: "Crescent-shaped tan leather hobo bag with a piston clasp",
          },
        },
        {
          href: "/es/blog/autenticar-un-bolso-gucci-es",
          title:
            "Cómo autenticar un bolso Gucci: 8 comprobaciones que puedes hacer tú mismo",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_gucci_bag_ai_2a67af1e93.jpg",
            width: 500,
            height: 281,
            alt: "Dark green leather shoulder bag with gold-tone hardware on linen",
          },
        },
        {
          href: "/es/blog/precio-bolso-gucci-segunda-mano-es",
          title:
            "¿Cuánto cuesta un bolso Gucci de segunda mano? Guía de precios y valor",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_bag_price_value_guide_ai_826a486dd5.jpg",
            width: 500,
            height: 281,
            alt: "Burgundy leather shoulder bag with gold-tone hardware on walnut",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/comprare-borsa-gucci-usata-guida-it",
          title:
            "Comprare una borsa Gucci usata: la mia guida onesta a tele, icone e prezzi",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_gucci_bags_buying_guide_ai_81efe96a4e.jpg",
            width: 500,
            height: 281,
            alt: "Tan, burgundy and green leather handbags on a walnut sideboard",
          },
        },
        {
          href: "/it/blog/gucci-gg-marmont-guida-it",
          title:
            "Guida alla Gucci GG Marmont: misure, il disegno del 2016 e l'acquisto usato",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_marmont_guide_ai_8d6efa0d90.jpg",
            width: 500,
            height: 281,
            alt: "Chevron-quilted dusty pink shoulder bag with a chain strap",
          },
        },
        {
          href: "/it/blog/gucci-jackie-guida-it",
          title:
            "Guida alla Gucci Jackie: l'hobo del 1961, il ritorno del 2020 e l'usato",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_jackie_guide_ai_ee5b8b6043.jpg",
            width: 500,
            height: 281,
            alt: "Crescent-shaped tan leather hobo bag with a piston clasp",
          },
        },
        {
          href: "/it/blog/autenticare-una-borsa-gucci-it",
          title:
            "Come autenticare una borsa Gucci: 8 controlli che puoi fare da solo",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_authenticate_a_gucci_bag_ai_2a67af1e93.jpg",
            width: 500,
            height: 281,
            alt: "Dark green leather shoulder bag with gold-tone hardware on linen",
          },
        },
        {
          href: "/it/blog/prezzo-borsa-gucci-usata-it",
          title: "Quanto costa una borsa Gucci usata? Guida a prezzi e valore",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_gucci_bag_price_value_guide_ai_826a486dd5.jpg",
            width: 500,
            height: 281,
            alt: "Burgundy leather shoulder bag with gold-tone hardware on walnut",
          },
        },
      ],
    },
  },
  {
    key: "/brands/bottega-veneta",
    urls: {
      en: "/brands/bottega-veneta",
      nl: "/merken/bottega-veneta",
      de: "/marken/bottega-veneta",
      fr: "/marques/bottega-veneta",
      es: "/marcas/bottega-veneta",
      it: "/marche/bottega-veneta",
    },
    links: {
      en: [
        {
          href: "/blog/secondhand-bottega-veneta-bags-guide",
          title:
            "Secondhand Bottega Veneta: My Guide to Buying, Checking and Pricing the Weave",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_bottega_veneta_bags_guide_ai_ca0be1088d.jpg",
            width: 500,
            height: 281,
            alt: "Woven caramel leather tote showing interlaced leather strips",
          },
        },
        {
          href: "/blog/secondhand-designer-handbags-guide",
          title:
            "The Definitive Guide to Buying Secondhand Designer Handbags in Europe",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/blog/handbag-authentication-guide",
          title:
            "How to Authenticate a Designer Handbag: The Cross-Brand Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/blog/how-to-care-for-leather-handbags",
          title:
            "How to Care for Leather Handbags: The Routine That Protects Their Value",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/tweedehands-bottega-veneta-tas-kopen-nl",
          title:
            "Tweedehands Bottega Veneta: mijn gids voor kopen, controleren en prijzen van het vlechtwerk",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_bottega_veneta_bags_guide_ai_ca0be1088d.jpg",
            width: 500,
            height: 281,
            alt: "Woven caramel leather tote showing interlaced leather strips",
          },
        },
        {
          href: "/nl/blog/tweedehands-designertas-kopen-complete-gids-nl",
          title:
            "De complete gids voor het kopen van een tweedehands designertas in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/nl/blog/designertas-authenticiteit-controleren-gids-nl",
          title:
            "Hoe herken je een echte designertas? De gids voor alle merken",
          readingMinutes: 12,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/nl/blog/leren-tassen-onderhouden-nl",
          title:
            "Leren tassen onderhouden: de routine die hun waarde beschermt",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/gebrauchte-bottega-veneta-tasche-kaufen-de",
          title:
            "Bottega Veneta gebraucht: mein Guide zu Kauf, Prüfung und Preisen des Intrecciato",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_bottega_veneta_bags_guide_ai_ca0be1088d.jpg",
            width: 500,
            height: 281,
            alt: "Woven caramel leather tote showing interlaced leather strips",
          },
        },
        {
          href: "/de/blog/gebrauchte-designertasche-kaufen-guide-de",
          title:
            "Der umfassende Guide zum Kauf gebrauchter Designertaschen in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/de/blog/designertasche-echtheit-pruefen-guide-de",
          title:
            "Designertasche auf Echtheit prüfen: der markenübergreifende Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/de/blog/ledertaschen-pflegen-de",
          title: "Ledertaschen pflegen: die Routine, die ihren Wert schützt",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/bottega-veneta-seconde-main-guide-fr",
          title:
            "Bottega Veneta seconde main : mon guide pour acheter, vérifier et situer le prix de l'intrecciato",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_bottega_veneta_bags_guide_ai_ca0be1088d.jpg",
            width: 500,
            height: 281,
            alt: "Woven caramel leather tote showing interlaced leather strips",
          },
        },
        {
          href: "/fr/blog/acheter-sac-de-luxe-seconde-main-guide-fr",
          title:
            "Le guide complet pour acheter un sac de luxe seconde main en Europe",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/fr/blog/authentifier-un-sac-de-luxe-guide-fr",
          title: "Comment authentifier un sac de luxe : le guide multimarques",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/fr/blog/entretien-sac-en-cuir-fr",
          title: "Entretenir un sac en cuir : la routine qui protège sa valeur",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/bottega-veneta-segunda-mano-guia-es",
          title:
            "Bottega Veneta de segunda mano: mi guía para comprar, comprobar y valorar el intrecciato",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_bottega_veneta_bags_guide_ai_ca0be1088d.jpg",
            width: 500,
            height: 281,
            alt: "Woven caramel leather tote showing interlaced leather strips",
          },
        },
        {
          href: "/es/blog/comprar-bolso-de-lujo-segunda-mano-guia-es",
          title:
            "La guía definitiva para comprar un bolso de lujo de segunda mano en Europa",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/es/blog/autenticar-un-bolso-de-lujo-guia-es",
          title:
            "Cómo autenticar un bolso de lujo: la guía para todas las marcas",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/es/blog/cuidar-bolsos-de-piel-es",
          title:
            "Cómo cuidar los bolsos de piel: la rutina que protege su valor",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/bottega-veneta-usata-guida-it",
          title:
            "Bottega Veneta usata: la mia guida per comprare, controllare e valutare l'intrecciato",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_bottega_veneta_bags_guide_ai_ca0be1088d.jpg",
            width: 500,
            height: 281,
            alt: "Woven caramel leather tote showing interlaced leather strips",
          },
        },
        {
          href: "/it/blog/comprare-borsa-firmata-usata-guida-it",
          title:
            "La guida definitiva per comprare una borsa firmata usata in Europa",
          readingMinutes: 15,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/it/blog/autenticare-una-borsa-firmata-guida-it",
          title:
            "Come autenticare una borsa firmata: la guida per tutte le maison",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/it/blog/come-curare-borse-in-pelle-it",
          title:
            "Come curare le borse in pelle: la routine che ne protegge il valore",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
    },
  },
  {
    key: "/brands/prada",
    urls: {
      en: "/brands/prada",
      nl: "/merken/prada",
      de: "/marken/prada",
      fr: "/marques/prada",
      es: "/marcas/prada",
      it: "/marche/prada",
    },
    links: {
      en: [
        {
          href: "/blog/secondhand-prada-bags-guide",
          title:
            "Secondhand Prada: My Guide to Buying, Checking and Pricing Nylon and Saffiano",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_prada_bags_guide_ai_7fc7a0fa65.jpg",
            width: 500,
            height: 281,
            alt: "Structured black crosshatch-textured leather tote with slim handles",
          },
        },
        {
          href: "/blog/secondhand-designer-handbags-guide",
          title:
            "The Definitive Guide to Buying Secondhand Designer Handbags in Europe",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/blog/handbag-authentication-guide",
          title:
            "How to Authenticate a Designer Handbag: The Cross-Brand Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/blog/how-to-care-for-leather-handbags",
          title:
            "How to Care for Leather Handbags: The Routine That Protects Their Value",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/tweedehands-prada-tas-kopen-nl",
          title:
            "Tweedehands Prada: mijn gids voor kopen, controleren en prijzen van nylon en saffiano",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_prada_bags_guide_ai_7fc7a0fa65.jpg",
            width: 500,
            height: 281,
            alt: "Structured black crosshatch-textured leather tote with slim handles",
          },
        },
        {
          href: "/nl/blog/tweedehands-designertas-kopen-complete-gids-nl",
          title:
            "De complete gids voor het kopen van een tweedehands designertas in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/nl/blog/designertas-authenticiteit-controleren-gids-nl",
          title:
            "Hoe herken je een echte designertas? De gids voor alle merken",
          readingMinutes: 12,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/nl/blog/leren-tassen-onderhouden-nl",
          title:
            "Leren tassen onderhouden: de routine die hun waarde beschermt",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/gebrauchte-prada-tasche-kaufen-de",
          title:
            "Prada gebraucht: mein Guide zu Kauf, Prüfung und Preisen von Nylon und Saffiano",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_prada_bags_guide_ai_7fc7a0fa65.jpg",
            width: 500,
            height: 281,
            alt: "Structured black crosshatch-textured leather tote with slim handles",
          },
        },
        {
          href: "/de/blog/gebrauchte-designertasche-kaufen-guide-de",
          title:
            "Der umfassende Guide zum Kauf gebrauchter Designertaschen in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/de/blog/designertasche-echtheit-pruefen-guide-de",
          title:
            "Designertasche auf Echtheit prüfen: der markenübergreifende Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/de/blog/ledertaschen-pflegen-de",
          title: "Ledertaschen pflegen: die Routine, die ihren Wert schützt",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/prada-seconde-main-guide-fr",
          title:
            "Prada seconde main : mon guide pour acheter, vérifier et situer le prix du nylon et du saffiano",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_prada_bags_guide_ai_7fc7a0fa65.jpg",
            width: 500,
            height: 281,
            alt: "Structured black crosshatch-textured leather tote with slim handles",
          },
        },
        {
          href: "/fr/blog/acheter-sac-de-luxe-seconde-main-guide-fr",
          title:
            "Le guide complet pour acheter un sac de luxe seconde main en Europe",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/fr/blog/authentifier-un-sac-de-luxe-guide-fr",
          title: "Comment authentifier un sac de luxe : le guide multimarques",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/fr/blog/entretien-sac-en-cuir-fr",
          title: "Entretenir un sac en cuir : la routine qui protège sa valeur",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/prada-segunda-mano-guia-es",
          title:
            "Prada de segunda mano: mi guía para comprar, comprobar y valorar el nailon y el saffiano",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_prada_bags_guide_ai_7fc7a0fa65.jpg",
            width: 500,
            height: 281,
            alt: "Structured black crosshatch-textured leather tote with slim handles",
          },
        },
        {
          href: "/es/blog/comprar-bolso-de-lujo-segunda-mano-guia-es",
          title:
            "La guía definitiva para comprar un bolso de lujo de segunda mano en Europa",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/es/blog/autenticar-un-bolso-de-lujo-guia-es",
          title:
            "Cómo autenticar un bolso de lujo: la guía para todas las marcas",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/es/blog/cuidar-bolsos-de-piel-es",
          title:
            "Cómo cuidar los bolsos de piel: la rutina que protege su valor",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/prada-usata-guida-it",
          title:
            "Prada usata: la mia guida per comprare, controllare e valutare nylon e saffiano",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_prada_bags_guide_ai_7fc7a0fa65.jpg",
            width: 500,
            height: 281,
            alt: "Structured black crosshatch-textured leather tote with slim handles",
          },
        },
        {
          href: "/it/blog/comprare-borsa-firmata-usata-guida-it",
          title:
            "La guida definitiva per comprare una borsa firmata usata in Europa",
          readingMinutes: 15,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/it/blog/autenticare-una-borsa-firmata-guida-it",
          title:
            "Come autenticare una borsa firmata: la guida per tutte le maison",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/it/blog/come-curare-borse-in-pelle-it",
          title:
            "Come curare le borse in pelle: la routine che ne protegge il valore",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
    },
  },
  {
    key: "/brands/balenciaga",
    urls: {
      en: "/brands/balenciaga",
      nl: "/merken/balenciaga",
      de: "/marken/balenciaga",
      fr: "/marques/balenciaga",
      es: "/marcas/balenciaga",
      it: "/marche/balenciaga",
    },
    links: {
      en: [
        {
          href: "/blog/secondhand-balenciaga-bags-guide",
          title:
            "Secondhand Balenciaga: My Guide to Buying, Checking and Pricing the Moto Era",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_balenciaga_bags_guide_ai_ab95515b70.jpg",
            width: 500,
            height: 281,
            alt: "Slouchy distressed charcoal lambskin bag with buckles and tassel",
          },
        },
        {
          href: "/blog/secondhand-designer-handbags-guide",
          title:
            "The Definitive Guide to Buying Secondhand Designer Handbags in Europe",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/blog/handbag-authentication-guide",
          title:
            "How to Authenticate a Designer Handbag: The Cross-Brand Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/blog/how-to-care-for-leather-handbags",
          title:
            "How to Care for Leather Handbags: The Routine That Protects Their Value",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/tweedehands-balenciaga-tas-kopen-nl",
          title:
            "Tweedehands Balenciaga: mijn gids voor kopen, controleren en prijzen van het moto-tijdperk",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_balenciaga_bags_guide_ai_ab95515b70.jpg",
            width: 500,
            height: 281,
            alt: "Slouchy distressed charcoal lambskin bag with buckles and tassel",
          },
        },
        {
          href: "/nl/blog/tweedehands-designertas-kopen-complete-gids-nl",
          title:
            "De complete gids voor het kopen van een tweedehands designertas in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/nl/blog/designertas-authenticiteit-controleren-gids-nl",
          title:
            "Hoe herken je een echte designertas? De gids voor alle merken",
          readingMinutes: 12,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/nl/blog/leren-tassen-onderhouden-nl",
          title:
            "Leren tassen onderhouden: de routine die hun waarde beschermt",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/gebrauchte-balenciaga-tasche-kaufen-de",
          title:
            "Balenciaga gebraucht: mein Guide zu Kauf, Prüfung und Preisen der Moto-Ära",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_balenciaga_bags_guide_ai_ab95515b70.jpg",
            width: 500,
            height: 281,
            alt: "Slouchy distressed charcoal lambskin bag with buckles and tassel",
          },
        },
        {
          href: "/de/blog/gebrauchte-designertasche-kaufen-guide-de",
          title:
            "Der umfassende Guide zum Kauf gebrauchter Designertaschen in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/de/blog/designertasche-echtheit-pruefen-guide-de",
          title:
            "Designertasche auf Echtheit prüfen: der markenübergreifende Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/de/blog/ledertaschen-pflegen-de",
          title: "Ledertaschen pflegen: die Routine, die ihren Wert schützt",
          readingMinutes: 7,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/balenciaga-seconde-main-guide-fr",
          title:
            "Balenciaga seconde main : mon guide pour acheter, vérifier et situer le prix de l'ère moto",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_balenciaga_bags_guide_ai_ab95515b70.jpg",
            width: 500,
            height: 281,
            alt: "Slouchy distressed charcoal lambskin bag with buckles and tassel",
          },
        },
        {
          href: "/fr/blog/acheter-sac-de-luxe-seconde-main-guide-fr",
          title:
            "Le guide complet pour acheter un sac de luxe seconde main en Europe",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/fr/blog/authentifier-un-sac-de-luxe-guide-fr",
          title: "Comment authentifier un sac de luxe : le guide multimarques",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/fr/blog/entretien-sac-en-cuir-fr",
          title: "Entretenir un sac en cuir : la routine qui protège sa valeur",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/balenciaga-segunda-mano-guia-es",
          title:
            "Balenciaga de segunda mano: mi guía para comprar, comprobar y valorar la era moto",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_balenciaga_bags_guide_ai_ab95515b70.jpg",
            width: 500,
            height: 281,
            alt: "Slouchy distressed charcoal lambskin bag with buckles and tassel",
          },
        },
        {
          href: "/es/blog/comprar-bolso-de-lujo-segunda-mano-guia-es",
          title:
            "La guía definitiva para comprar un bolso de lujo de segunda mano en Europa",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/es/blog/autenticar-un-bolso-de-lujo-guia-es",
          title:
            "Cómo autenticar un bolso de lujo: la guía para todas las marcas",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/es/blog/cuidar-bolsos-de-piel-es",
          title:
            "Cómo cuidar los bolsos de piel: la rutina que protege su valor",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/balenciaga-usata-guida-it",
          title:
            "Balenciaga usata: la mia guida per comprare, controllare e valutare l'era moto",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_balenciaga_bags_guide_ai_ab95515b70.jpg",
            width: 500,
            height: 281,
            alt: "Slouchy distressed charcoal lambskin bag with buckles and tassel",
          },
        },
        {
          href: "/it/blog/comprare-borsa-firmata-usata-guida-it",
          title:
            "La guida definitiva per comprare una borsa firmata usata in Europa",
          readingMinutes: 15,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/it/blog/autenticare-una-borsa-firmata-guida-it",
          title:
            "Come autenticare una borsa firmata: la guida per tutte le maison",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
        {
          href: "/it/blog/come-curare-borse-in-pelle-it",
          title:
            "Come curare le borse in pelle: la routine che ne protegge il valore",
          readingMinutes: 8,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_how_to_care_for_leather_handbags_ai_beccbd40a3.jpg",
            width: 500,
            height: 281,
            alt: "Tan leather handbag on a workbench with cloth, brush and balm",
          },
        },
      ],
    },
  },
  {
    key: "/brands/fendi",
    urls: {
      en: "/brands/fendi",
      nl: "/merken/fendi",
      de: "/marken/fendi",
      fr: "/marques/fendi",
      es: "/marcas/fendi",
      it: "/marche/fendi",
    },
    links: {
      en: [
        {
          href: "/blog/secondhand-fendi-valentino-bags-guide",
          title:
            "Secondhand Fendi and Valentino: My Guide to Rome's Two Bag Houses",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_fendi_valentino_bags_guide_ai_7fcb3fbae2.jpg",
            width: 500,
            height: 281,
            alt: "Caramel baguette shoulder bag and blush studded flap bag on linen",
          },
        },
        {
          href: "/blog/secondhand-designer-handbags-guide",
          title:
            "The Definitive Guide to Buying Secondhand Designer Handbags in Europe",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/blog/handbag-authentication-guide",
          title:
            "How to Authenticate a Designer Handbag: The Cross-Brand Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/tweedehands-fendi-valentino-tas-kopen-nl",
          title:
            "Tweedehands Fendi en Valentino: mijn gids voor de twee tassenhuizen van Rome",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_fendi_valentino_bags_guide_ai_7fcb3fbae2.jpg",
            width: 500,
            height: 281,
            alt: "Caramel baguette shoulder bag and blush studded flap bag on linen",
          },
        },
        {
          href: "/nl/blog/tweedehands-designertas-kopen-complete-gids-nl",
          title:
            "De complete gids voor het kopen van een tweedehands designertas in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/nl/blog/designertas-authenticiteit-controleren-gids-nl",
          title:
            "Hoe herken je een echte designertas? De gids voor alle merken",
          readingMinutes: 12,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/gebrauchte-fendi-valentino-tasche-kaufen-de",
          title:
            "Fendi und Valentino gebraucht: mein Guide zu Roms zwei Taschenhäusern",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_fendi_valentino_bags_guide_ai_7fcb3fbae2.jpg",
            width: 500,
            height: 281,
            alt: "Caramel baguette shoulder bag and blush studded flap bag on linen",
          },
        },
        {
          href: "/de/blog/gebrauchte-designertasche-kaufen-guide-de",
          title:
            "Der umfassende Guide zum Kauf gebrauchter Designertaschen in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/de/blog/designertasche-echtheit-pruefen-guide-de",
          title:
            "Designertasche auf Echtheit prüfen: der markenübergreifende Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/fendi-valentino-seconde-main-guide-fr",
          title:
            "Fendi et Valentino seconde main : mon guide des deux maisons romaines du sac",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_fendi_valentino_bags_guide_ai_7fcb3fbae2.jpg",
            width: 500,
            height: 281,
            alt: "Caramel baguette shoulder bag and blush studded flap bag on linen",
          },
        },
        {
          href: "/fr/blog/acheter-sac-de-luxe-seconde-main-guide-fr",
          title:
            "Le guide complet pour acheter un sac de luxe seconde main en Europe",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/fr/blog/authentifier-un-sac-de-luxe-guide-fr",
          title: "Comment authentifier un sac de luxe : le guide multimarques",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/fendi-valentino-segunda-mano-guia-es",
          title:
            "Fendi y Valentino de segunda mano: mi guía de las dos casas romanas del bolso",
          readingMinutes: 10,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_fendi_valentino_bags_guide_ai_7fcb3fbae2.jpg",
            width: 500,
            height: 281,
            alt: "Caramel baguette shoulder bag and blush studded flap bag on linen",
          },
        },
        {
          href: "/es/blog/comprar-bolso-de-lujo-segunda-mano-guia-es",
          title:
            "La guía definitiva para comprar un bolso de lujo de segunda mano en Europa",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/es/blog/autenticar-un-bolso-de-lujo-guia-es",
          title:
            "Cómo autenticar un bolso de lujo: la guía para todas las marcas",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/fendi-valentino-usate-guida-it",
          title:
            "Fendi e Valentino usate: la mia guida alle due maison romane della borsa",
          readingMinutes: 10,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_fendi_valentino_bags_guide_ai_7fcb3fbae2.jpg",
            width: 500,
            height: 281,
            alt: "Caramel baguette shoulder bag and blush studded flap bag on linen",
          },
        },
        {
          href: "/it/blog/comprare-borsa-firmata-usata-guida-it",
          title:
            "La guida definitiva per comprare una borsa firmata usata in Europa",
          readingMinutes: 15,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/it/blog/autenticare-una-borsa-firmata-guida-it",
          title:
            "Come autenticare una borsa firmata: la guida per tutte le maison",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
    },
  },
  {
    key: "/brands/valentino",
    urls: {
      en: "/brands/valentino",
      nl: "/merken/valentino",
      de: "/marken/valentino",
      fr: "/marques/valentino",
      es: "/marcas/valentino",
      it: "/marche/valentino",
    },
    links: {
      en: [
        {
          href: "/blog/secondhand-fendi-valentino-bags-guide",
          title:
            "Secondhand Fendi and Valentino: My Guide to Rome's Two Bag Houses",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_fendi_valentino_bags_guide_ai_7fcb3fbae2.jpg",
            width: 500,
            height: 281,
            alt: "Caramel baguette shoulder bag and blush studded flap bag on linen",
          },
        },
        {
          href: "/blog/secondhand-designer-handbags-guide",
          title:
            "The Definitive Guide to Buying Secondhand Designer Handbags in Europe",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/blog/handbag-authentication-guide",
          title:
            "How to Authenticate a Designer Handbag: The Cross-Brand Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/tweedehands-fendi-valentino-tas-kopen-nl",
          title:
            "Tweedehands Fendi en Valentino: mijn gids voor de twee tassenhuizen van Rome",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_fendi_valentino_bags_guide_ai_7fcb3fbae2.jpg",
            width: 500,
            height: 281,
            alt: "Caramel baguette shoulder bag and blush studded flap bag on linen",
          },
        },
        {
          href: "/nl/blog/tweedehands-designertas-kopen-complete-gids-nl",
          title:
            "De complete gids voor het kopen van een tweedehands designertas in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/nl/blog/designertas-authenticiteit-controleren-gids-nl",
          title:
            "Hoe herken je een echte designertas? De gids voor alle merken",
          readingMinutes: 12,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/gebrauchte-fendi-valentino-tasche-kaufen-de",
          title:
            "Fendi und Valentino gebraucht: mein Guide zu Roms zwei Taschenhäusern",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_fendi_valentino_bags_guide_ai_7fcb3fbae2.jpg",
            width: 500,
            height: 281,
            alt: "Caramel baguette shoulder bag and blush studded flap bag on linen",
          },
        },
        {
          href: "/de/blog/gebrauchte-designertasche-kaufen-guide-de",
          title:
            "Der umfassende Guide zum Kauf gebrauchter Designertaschen in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/de/blog/designertasche-echtheit-pruefen-guide-de",
          title:
            "Designertasche auf Echtheit prüfen: der markenübergreifende Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/fendi-valentino-seconde-main-guide-fr",
          title:
            "Fendi et Valentino seconde main : mon guide des deux maisons romaines du sac",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_fendi_valentino_bags_guide_ai_7fcb3fbae2.jpg",
            width: 500,
            height: 281,
            alt: "Caramel baguette shoulder bag and blush studded flap bag on linen",
          },
        },
        {
          href: "/fr/blog/acheter-sac-de-luxe-seconde-main-guide-fr",
          title:
            "Le guide complet pour acheter un sac de luxe seconde main en Europe",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/fr/blog/authentifier-un-sac-de-luxe-guide-fr",
          title: "Comment authentifier un sac de luxe : le guide multimarques",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/fendi-valentino-segunda-mano-guia-es",
          title:
            "Fendi y Valentino de segunda mano: mi guía de las dos casas romanas del bolso",
          readingMinutes: 10,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_fendi_valentino_bags_guide_ai_7fcb3fbae2.jpg",
            width: 500,
            height: 281,
            alt: "Caramel baguette shoulder bag and blush studded flap bag on linen",
          },
        },
        {
          href: "/es/blog/comprar-bolso-de-lujo-segunda-mano-guia-es",
          title:
            "La guía definitiva para comprar un bolso de lujo de segunda mano en Europa",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/es/blog/autenticar-un-bolso-de-lujo-guia-es",
          title:
            "Cómo autenticar un bolso de lujo: la guía para todas las marcas",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/fendi-valentino-usate-guida-it",
          title:
            "Fendi e Valentino usate: la mia guida alle due maison romane della borsa",
          readingMinutes: 10,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_fendi_valentino_bags_guide_ai_7fcb3fbae2.jpg",
            width: 500,
            height: 281,
            alt: "Caramel baguette shoulder bag and blush studded flap bag on linen",
          },
        },
        {
          href: "/it/blog/comprare-borsa-firmata-usata-guida-it",
          title:
            "La guida definitiva per comprare una borsa firmata usata in Europa",
          readingMinutes: 15,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/it/blog/autenticare-una-borsa-firmata-guida-it",
          title:
            "Come autenticare una borsa firmata: la guida per tutte le maison",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
    },
  },
  {
    key: "/brands/celine",
    urls: {
      en: "/brands/celine",
      nl: "/merken/celine",
      de: "/marken/celine",
      fr: "/marques/celine",
      es: "/marcas/celine",
      it: "/marche/celine",
    },
    links: {
      en: [
        {
          href: "/blog/secondhand-celine-saint-laurent-bags-guide",
          title:
            "Secondhand Celine and Saint Laurent: My Guide to the Paris Minimalists",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_celine_saint_laurent_bags_guide_ai_121bc8e0fb.jpg",
            width: 500,
            height: 281,
            alt: "Boxy tan top-handle bag and quilted black chain bag on oak",
          },
        },
        {
          href: "/blog/secondhand-designer-handbags-guide",
          title:
            "The Definitive Guide to Buying Secondhand Designer Handbags in Europe",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/blog/handbag-authentication-guide",
          title:
            "How to Authenticate a Designer Handbag: The Cross-Brand Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/tweedehands-celine-saint-laurent-tas-kopen-nl",
          title:
            "Tweedehands Celine en Saint Laurent: mijn gids voor de Parijse minimalisten",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_celine_saint_laurent_bags_guide_ai_121bc8e0fb.jpg",
            width: 500,
            height: 281,
            alt: "Boxy tan top-handle bag and quilted black chain bag on oak",
          },
        },
        {
          href: "/nl/blog/tweedehands-designertas-kopen-complete-gids-nl",
          title:
            "De complete gids voor het kopen van een tweedehands designertas in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/nl/blog/designertas-authenticiteit-controleren-gids-nl",
          title:
            "Hoe herken je een echte designertas? De gids voor alle merken",
          readingMinutes: 12,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/gebrauchte-celine-saint-laurent-tasche-kaufen-de",
          title:
            "Celine und Saint Laurent gebraucht: mein Guide zu den Pariser Minimalisten",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_celine_saint_laurent_bags_guide_ai_121bc8e0fb.jpg",
            width: 500,
            height: 281,
            alt: "Boxy tan top-handle bag and quilted black chain bag on oak",
          },
        },
        {
          href: "/de/blog/gebrauchte-designertasche-kaufen-guide-de",
          title:
            "Der umfassende Guide zum Kauf gebrauchter Designertaschen in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/de/blog/designertasche-echtheit-pruefen-guide-de",
          title:
            "Designertasche auf Echtheit prüfen: der markenübergreifende Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/celine-saint-laurent-seconde-main-guide-fr",
          title:
            "Celine et Saint Laurent seconde main : mon guide des minimalistes parisiens",
          readingMinutes: 10,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_celine_saint_laurent_bags_guide_ai_121bc8e0fb.jpg",
            width: 500,
            height: 281,
            alt: "Boxy tan top-handle bag and quilted black chain bag on oak",
          },
        },
        {
          href: "/fr/blog/acheter-sac-de-luxe-seconde-main-guide-fr",
          title:
            "Le guide complet pour acheter un sac de luxe seconde main en Europe",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/fr/blog/authentifier-un-sac-de-luxe-guide-fr",
          title: "Comment authentifier un sac de luxe : le guide multimarques",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/celine-saint-laurent-segunda-mano-guia-es",
          title:
            "Celine y Saint Laurent de segunda mano: mi guía de las minimalistas de París",
          readingMinutes: 10,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_celine_saint_laurent_bags_guide_ai_121bc8e0fb.jpg",
            width: 500,
            height: 281,
            alt: "Boxy tan top-handle bag and quilted black chain bag on oak",
          },
        },
        {
          href: "/es/blog/comprar-bolso-de-lujo-segunda-mano-guia-es",
          title:
            "La guía definitiva para comprar un bolso de lujo de segunda mano en Europa",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/es/blog/autenticar-un-bolso-de-lujo-guia-es",
          title:
            "Cómo autenticar un bolso de lujo: la guía para todas las marcas",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/celine-saint-laurent-usate-guida-it",
          title:
            "Celine e Saint Laurent usate: la mia guida alle minimaliste di Parigi",
          readingMinutes: 10,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_celine_saint_laurent_bags_guide_ai_121bc8e0fb.jpg",
            width: 500,
            height: 281,
            alt: "Boxy tan top-handle bag and quilted black chain bag on oak",
          },
        },
        {
          href: "/it/blog/comprare-borsa-firmata-usata-guida-it",
          title:
            "La guida definitiva per comprare una borsa firmata usata in Europa",
          readingMinutes: 15,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/it/blog/autenticare-una-borsa-firmata-guida-it",
          title:
            "Come autenticare una borsa firmata: la guida per tutte le maison",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
    },
  },
  {
    key: "/brands/saint-laurent",
    urls: {
      en: "/brands/saint-laurent",
      nl: "/merken/saint-laurent",
      de: "/marken/saint-laurent",
      fr: "/marques/saint-laurent",
      es: "/marcas/saint-laurent",
      it: "/marche/saint-laurent",
    },
    links: {
      en: [
        {
          href: "/blog/secondhand-celine-saint-laurent-bags-guide",
          title:
            "Secondhand Celine and Saint Laurent: My Guide to the Paris Minimalists",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_celine_saint_laurent_bags_guide_ai_121bc8e0fb.jpg",
            width: 500,
            height: 281,
            alt: "Boxy tan top-handle bag and quilted black chain bag on oak",
          },
        },
        {
          href: "/blog/secondhand-designer-handbags-guide",
          title:
            "The Definitive Guide to Buying Secondhand Designer Handbags in Europe",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/blog/handbag-authentication-guide",
          title:
            "How to Authenticate a Designer Handbag: The Cross-Brand Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
      nl: [
        {
          href: "/nl/blog/tweedehands-celine-saint-laurent-tas-kopen-nl",
          title:
            "Tweedehands Celine en Saint Laurent: mijn gids voor de Parijse minimalisten",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_celine_saint_laurent_bags_guide_ai_121bc8e0fb.jpg",
            width: 500,
            height: 281,
            alt: "Boxy tan top-handle bag and quilted black chain bag on oak",
          },
        },
        {
          href: "/nl/blog/tweedehands-designertas-kopen-complete-gids-nl",
          title:
            "De complete gids voor het kopen van een tweedehands designertas in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/nl/blog/designertas-authenticiteit-controleren-gids-nl",
          title:
            "Hoe herken je een echte designertas? De gids voor alle merken",
          readingMinutes: 12,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
      de: [
        {
          href: "/de/blog/gebrauchte-celine-saint-laurent-tasche-kaufen-de",
          title:
            "Celine und Saint Laurent gebraucht: mein Guide zu den Pariser Minimalisten",
          readingMinutes: 9,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_celine_saint_laurent_bags_guide_ai_121bc8e0fb.jpg",
            width: 500,
            height: 281,
            alt: "Boxy tan top-handle bag and quilted black chain bag on oak",
          },
        },
        {
          href: "/de/blog/gebrauchte-designertasche-kaufen-guide-de",
          title:
            "Der umfassende Guide zum Kauf gebrauchter Designertaschen in Europa",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/de/blog/designertasche-echtheit-pruefen-guide-de",
          title:
            "Designertasche auf Echtheit prüfen: der markenübergreifende Guide",
          readingMinutes: 11,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
      fr: [
        {
          href: "/fr/blog/celine-saint-laurent-seconde-main-guide-fr",
          title:
            "Celine et Saint Laurent seconde main : mon guide des minimalistes parisiens",
          readingMinutes: 10,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_celine_saint_laurent_bags_guide_ai_121bc8e0fb.jpg",
            width: 500,
            height: 281,
            alt: "Boxy tan top-handle bag and quilted black chain bag on oak",
          },
        },
        {
          href: "/fr/blog/acheter-sac-de-luxe-seconde-main-guide-fr",
          title:
            "Le guide complet pour acheter un sac de luxe seconde main en Europe",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/fr/blog/authentifier-un-sac-de-luxe-guide-fr",
          title: "Comment authentifier un sac de luxe : le guide multimarques",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
      es: [
        {
          href: "/es/blog/celine-saint-laurent-segunda-mano-guia-es",
          title:
            "Celine y Saint Laurent de segunda mano: mi guía de las minimalistas de París",
          readingMinutes: 10,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_celine_saint_laurent_bags_guide_ai_121bc8e0fb.jpg",
            width: 500,
            height: 281,
            alt: "Boxy tan top-handle bag and quilted black chain bag on oak",
          },
        },
        {
          href: "/es/blog/comprar-bolso-de-lujo-segunda-mano-guia-es",
          title:
            "La guía definitiva para comprar un bolso de lujo de segunda mano en Europa",
          readingMinutes: 16,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/es/blog/autenticar-un-bolso-de-lujo-guia-es",
          title:
            "Cómo autenticar un bolso de lujo: la guía para todas las marcas",
          readingMinutes: 14,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
      it: [
        {
          href: "/it/blog/celine-saint-laurent-usate-guida-it",
          title:
            "Celine e Saint Laurent usate: la mia guida alle minimaliste di Parigi",
          readingMinutes: 10,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_celine_saint_laurent_bags_guide_ai_121bc8e0fb.jpg",
            width: 500,
            height: 281,
            alt: "Boxy tan top-handle bag and quilted black chain bag on oak",
          },
        },
        {
          href: "/it/blog/comprare-borsa-firmata-usata-guida-it",
          title:
            "La guida definitiva per comprare una borsa firmata usata in Europa",
          readingMinutes: 15,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_secondhand_designer_handbags_guide_ai_c64b160543.jpg",
            width: 500,
            height: 281,
            alt: "Five leather handbags in muted tones arranged on beige linen",
          },
        },
        {
          href: "/it/blog/autenticare-una-borsa-firmata-guida-it",
          title:
            "Come autenticare una borsa firmata: la guida per tutte le maison",
          readingMinutes: 13,
          image: {
            url: "https://storage.googleapis.com/whoppah-strapi/small_handbag_authentication_guide_ai_841bff546c.jpg",
            width: 500,
            height: 281,
            alt: "Magnifying loupe on caramel leather beside brass hardware",
          },
        },
      ],
    },
  },
];

const LOOKUP: Record<string, PlpEditorialLink[]> = {};

for (const entry of PLP_EDITORIAL_ENTRIES) {
  for (const locale of PLP_EDITORIAL_LOCALES) {
    LOOKUP[`${locale}:${entry.urls[locale]}`] = entry.links[locale];
  }
}

/** Strip query string and a trailing slash so /bags?page=2 still matches. */
export const normalizePlpEditorialUrl = (url: string) => {
  const path = url.split("?")[0] ?? url;

  if (path.length > 1 && path.endsWith("/")) {
    return path.slice(0, -1);
  }

  return path;
};

/**
 * Links for a PLP identified the same way Strapi listing-pages are: locale
 * plus the localized path without a locale prefix (listingPageVariables.url).
 * Returns undefined when the PLP has no mapping.
 */
export const getPlpEditorialLinks = (
  locale: string | undefined,
  url: string | undefined
): PlpEditorialLink[] | undefined => {
  if (!locale || !url) {
    return undefined;
  }

  const links = LOOKUP[`${locale}:${normalizePlpEditorialUrl(url)}`];

  return links && links.length > 0 ? links : undefined;
};
